/*
 * MDI intake form renderer.
 *
 * Takes over the MD Integrations intake form. Rather than the MDI Shopify app rendering the
 * questionnaire through a shortcode, we render the entire flow ourselves from a JSON document.
 *
 * The document shape is documented in docs/mdi-intake-form.md. Nothing in this file is specific
 * to the current questions: screens, branching, outcomes, and the patient-record form fields all
 * come from the parsed config. That keeps the renderer portable for when the flow is served by the
 * webhook-orchestrator instead of hardcoded in the theme.
 *
 * Config sources, in priority order:
 *   1. data-config-url  -> fetched at runtime (the orchestrator API seam). Embedded JSON is the
 *                          fallback if the request fails.
 *   2. embedded <script type="application/json" data-mdi-config> -> the hardcoded default today.
 */
// Progress snapshot (sessionStorage, this tab only) so an iOS swipe-back to the product page or a
// reload resumes the run instead of restarting it with every typed detail gone. Dropped on Start
// over, on any outcome, and after PROGRESS_MAX_AGE_MS.
const PROGRESS_KEY = "mdi-intake-progress";
const PROGRESS_MAX_AGE_MS = 2 * 60 * 60 * 1000;

class MdiIntakeForm extends HTMLElement {
  connectedCallback() {
    if (this._booted) return;
    this._booted = true;

    this.root = this.querySelector("[data-mdi-root]");
    this.progressBar = this.querySelector(".mdi-intake__progress");
    this.progressFill = this.querySelector("[data-mdi-progress]");
    this.statusEl = this.querySelector("[data-mdi-status]");

    this.configUrl = this.dataset.configUrl || null;
    this.submitUrl = this.dataset.submitUrl || null;
    // The internal triage code is staff-only. It is server-gated (default off) so a logged-out
    // customer never receives it; the theme editor / a merchant setting turns it on for staff.
    this.showTriage = this.dataset.showTriage === "true";

    // Commerce handoff (all server-provided; the renderer stays store-agnostic).
    this.loggedIn = this.dataset.loggedIn === "true";
    // One Starter Kit per rolling 12 months. Server-resolved from the customer metafield
    // custom.starter_kit_eligible (see snippets/mdi-intake-form.liquid). Fail-open: the attribute is
    // present only when the customer is explicitly ineligible, so guests / eligible customers are
    // never flagged here. When set, a passing outcome shows the limit screen instead of adding the
    // kit and going to checkout; the cart & checkout validation Function stays the authoritative block.
    this.starterKitIneligible = this.dataset.starterKitIneligible === "true";
    // Post-purchase ("complete") mode: the shopper already has a Starter Kit order with no intake
    // behind it (a pre-launch cart, a cart permalink, a wallet button). Entered from the order email /
    // thank-you link (?complete=1, via the orchestrator's chat route) or when the partial sees a
    // customer with a Starter Kit order and no intake stamp. The questionnaire runs as usual, the
    // record is submitted so the orchestrator can create the MDI case for the pending order, and the
    // flow ends there: no cart add, no checkout, no 12-month gate (their own order set that flag).
    this.completeMode = this.dataset.pendingOrder === "true" || this.hasCompleteParam();
    // Proof of intake returned by the submit route; travels on the Starter Kit line as `_intake`.
    this.intakeToken = null;
    this.starterKitVariantId = this.dataset.starterKitVariantId || null;
    this.loginUrl = this.dataset.loginUrl || null;
    this.customerId = this.dataset.customerId || null;
    // Market: "US" runs the medical flow (record form, MDI). Any market whose config says
    // mode "educational" (Canada) runs the same questions as an optional quiz: no record form,
    // no MDI, every outcome offers "Continue to purchase". Resolved once the config is loaded.
    this.market = (this.dataset.market || "US").toUpperCase();
    this.marketConfig = {};
    this.mode = "medical";
    this.checkoutUrl = this.dataset.checkoutUrl || "/checkout";
    // Storefront API (public token) used to create a prefilled checkout cart; null = standard cart.
    this.storefrontToken = this.dataset.storefrontToken || null;
    this.shopDomain = this.dataset.shopDomain || null;
    this.cartAddUrl = this.dataset.cartAddUrl || "/cart/add.js";
    this.placesEnabled = this.dataset.places === "true";
    // Lead hand-off endpoint (name + email captured up front). The contact is posted only when the
    // shopper clicks "Print page" on an outcome, not at intake start, so starting the questionnaire
    // never triggers the lead journey's intake-abandon email.
    this.leadUrl = this.dataset.leadUrl || null;
    this.leadCaptured = null;
    // CRM routing endpoint (App Proxy / orchestrator). Receives the triage event; no secret here.
    this.routingUrl = this.dataset.routingUrl || null;
    // Step events (funnel). One post per forward screen transition, chained by a per-run session id,
    // so the Orca report can rebuild each run's path and see where people stop. Ids and option labels
    // only, never PHI. Optional: without event_url the steps are only dispatched as DOM events.
    this.eventUrl = this.dataset.eventUrl || null;
    this.sessionId = null;
    // One-shot hints for the next render(): who the step is "from" (proceedAnyway sets the outcome id)
    // and which answer label led there (recordAnswer sets it). Cleared as soon as render() reads them.
    this._stepFromOverride = null;
    this._stepAnswer = null;

    this.history = [];
    this.flags = [];
    this.values = {};
    this.current = null;
    // Answer trail powers the review screen and per-question editing. answerOrder keeps path order;
    // answers holds the displayable choice; flagByAnswer maps each answer to any prescriber flag it
    // set, so flags can be rebuilt exactly whenever the trail is truncated (edit / back / re-answer).
    this.answers = new Map();
    this.answerOrder = [];
    this.flagByAnswer = new Map();
    this.itemFlagsByAnswer = new Map();

    this.loadConfig()
      .then((config) => {
        this.config = config;
        // There can be more than one form screen now (an up-front email capture and the full record).
        // reviewFormId is the record form that owns the review + final submission.
        const formEntries = Object.entries(config.screens || {}).filter(([, s]) => s.type === "form");
        this.formIds = formEntries.map(([id]) => id);
        const reviewEntry = formEntries.find(([, s]) => s.review);
        this.reviewFormId = reviewEntry ? reviewEntry[0] : this.formIds[0] || null;
        this.formId = this.reviewFormId;
        this.start({ resume: true });
      })
      .catch((err) => {
        console.error("[mdi-intake] could not load config", err);
        this.renderError();
      });
  }

  /* ---------------- Config loading ---------------- */

  async loadConfig() {
    const embedded = this.parseEmbeddedConfig();

    // Runtime fetch is the orchestrator seam. When data-config-url is set we prefer the API and
    // fall back to the embedded document so a failed request never leaves a blank form.
    if (this.configUrl) {
      try {
        const res = await fetch(this.configUrl, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const remote = await res.json();
        if (remote && remote.screens) return remote;
        throw new Error("config missing screens");
      } catch (err) {
        console.warn("[mdi-intake] config_url fetch failed, using embedded config", err);
      }
    }

    if (!embedded) throw new Error("no embedded config found");
    return embedded;
  }

  parseEmbeddedConfig() {
    const node = this.querySelector("[data-mdi-config]");
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (err) {
      console.error("[mdi-intake] embedded config is not valid JSON", err);
      return null;
    }
  }

  /* ---------------- Boot / navigation ---------------- */

  // A plain start() is a new run ("Start over"): it drops any saved progress. The boot passes
  // { resume: true } to pick up where a swipe-back or reload left off.
  start(opts = {}) {
    this.flags = [];
    this.values = {};
    this.history = [];
    this.current = null;
    this.answers = new Map();
    this.answerOrder = [];
    this.flagByAnswer = new Map();
    this.itemFlagsByAnswer = new Map();
    this.editReturn = null;
    this.leadCaptured = null;
    this._lastLead = null;
    this._lastLeadPosted = null;
    this._lastPayload = null;
    this._autoProceedFired = false;
    this._submitFailureShown = false;
    this._routed = false;
    this._keepInTouchPosted = null;
    // A new run, a new session: "Start over" must never chain onto the previous run's steps.
    this.sessionId = this.newSessionId();
    this._stepFromOverride = null;
    this._stepAnswer = null;
    const markets = (this.config.meta && this.config.meta.markets) || {};
    this.marketConfig = markets[this.market] || markets.US || {};
    this.mode = this.marketConfig.mode || "medical";

    // Entry gate for the rolling 12-month Starter Kit limit. Every purchase CTA now lands here, so a
    // customer the backend has marked ineligible (custom.starter_kit_eligible = false, resolved
    // server-side into data-starter-kit-ineligible) sees the limit immediately instead of answering
    // the whole questionnaire first. Applies to both modes: the educational quiz also ends at checkout.
    if (this.starterKitIneligible && !this.completeMode) {
      const limitId = this.limitOutcomeId();
      this.current = limitId;
      this.setStatusAndProgress("outcome", limitId);
      this.renderLimit(this.config.outcomes && this.config.outcomes[limitId], { entry: true });
      this.dispatch("limit", { entry: true, contact: null });
      return;
    }

    if (opts.resume && this.restoreProgress()) {
      this.render(this.current, true);
      return;
    }
    this.clearProgress();
    this.render(this.startScreenId());
  }

  /* ---------------- Progress snapshot ---------------- */

  progressStore() {
    try {
      return typeof sessionStorage !== "undefined" && sessionStorage ? sessionStorage : null;
    } catch (e) {
      return null; // storage disabled (private mode quirks, sandboxed frames)
    }
  }

  saveProgress() {
    const store = this.progressStore();
    if (!store || !this.current) return;
    const current = this.current === "__review__" ? this.reviewFormId : this.current;
    if (!current || !this.config.screens || !this.config.screens[current]) return;
    // Nothing to resume until a question is answered or a field typed into.
    if (!this.history.length && !Object.keys(this.values).some((k) => this.values[k])) return;
    const snap = {
      v: 1,
      savedAt: Date.now(),
      market: this.market,
      mode: this.mode,
      complete: !!this.completeMode,
      sessionId: this.sessionId,
      current,
      history: this.history.slice(),
      values: this.values,
      answerOrder: this.answerOrder.slice(),
      answers: Array.from(this.answers.entries()),
      flagByAnswer: Array.from(this.flagByAnswer.entries()),
      itemFlagsByAnswer: Array.from(this.itemFlagsByAnswer.entries()),
      leadCaptured: this.leadCaptured,
    };
    try {
      store.setItem(PROGRESS_KEY, JSON.stringify(snap));
    } catch (e) {
      /* quota / disabled: the run simply is not resumable */
    }
  }

  clearProgress() {
    const store = this.progressStore();
    if (!store) return;
    try {
      store.removeItem(PROGRESS_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  // The saved snapshot when it belongs to this market, mode and run type, is fresh, and points at a
  // screen that still exists (never an outcome). Anything else is ignored.
  readProgress() {
    const store = this.progressStore();
    if (!store) return null;
    let snap = null;
    try {
      snap = JSON.parse(store.getItem(PROGRESS_KEY) || "null");
    } catch (e) {
      return null;
    }
    if (!snap || snap.v !== 1 || typeof snap.current !== "string") return null;
    if (snap.market !== this.market || snap.mode !== this.mode || !!snap.complete !== !!this.completeMode) return null;
    if (!snap.savedAt || Date.now() - snap.savedAt > PROGRESS_MAX_AGE_MS) return null;
    const screens = this.config.screens || {};
    if (!screens[snap.current] || (this.config.outcomes && this.config.outcomes[snap.current])) return null;
    return snap;
  }

  restoreProgress() {
    const snap = this.readProgress();
    if (!snap) return false;
    this.sessionId = snap.sessionId || this.sessionId;
    this.current = snap.current;
    this.history = Array.isArray(snap.history) ? snap.history.slice() : [];
    this.values = snap.values && typeof snap.values === "object" ? snap.values : {};
    this.answerOrder = Array.isArray(snap.answerOrder) ? snap.answerOrder.slice() : [];
    this.answers = new Map(Array.isArray(snap.answers) ? snap.answers : []);
    this.flagByAnswer = new Map(Array.isArray(snap.flagByAnswer) ? snap.flagByAnswer : []);
    this.itemFlagsByAnswer = new Map(Array.isArray(snap.itemFlagsByAnswer) ? snap.itemFlagsByAnswer : []);
    this.leadCaptured = snap.leadCaptured || null;
    this.recomputeFlags();
    return true;
  }

  // A keystroke in a form field: keep the value so a resume (or the review) has it. The form's own
  // submit still trims and validates.
  noteFieldInput(field, control) {
    this.values[field.id] = control && control.value != null ? control.value : "";
    this.saveProgress();
  }

  hasCompleteParam() {
    try {
      return typeof window !== "undefined" && !!window.location && /[?&]complete=1(?:&|$)/.test(window.location.search);
    } catch (e) {
      return false;
    }
  }

  // First screen, where the config says. Form screens the educational quiz must not show are hopped
  // over in render() (educationalHopTarget), wherever they sit in the flow.
  startScreenId() {
    return (this.config.meta && this.config.meta.start) || Object.keys(this.config.screens || {})[0];
  }

  // Where an educational run goes instead of a screen it must not show: a lead-capture form hops to
  // its onComplete, the record form hops to the market's done screen, and a message interstitial
  // that only introduces a skipped form (the "good fit, next a few details" screen) hops wherever
  // that form hops, so the shopper is not shown the result twice. null when nothing is skipped.
  educationalHopTarget(id) {
    if (!this.isEducational) return null;
    const screen = this.config.screens && this.config.screens[id];
    if (!screen) return null;
    if (screen.type === "message") {
      const next = screen.go && this.educationalHopTarget(screen.go);
      return next || null;
    }
    if (screen.type !== "form") return null;
    if (id === this.reviewFormId) return this.marketConfig.doneScreen || "out_educational";
    if (screen.lead && screen.onComplete) return screen.onComplete;
    return null;
  }

  // The outcome that owns the limit-screen copy: the green outcome with the checkout handoff
  // ("proceed"), the same one renderOutcome gates on when a passing customer is ineligible.
  limitOutcomeId() {
    const outcomes = this.config.outcomes || {};
    const ids = Object.keys(outcomes);
    return ids.find((id) => outcomes[id].green && outcomes[id].action === "proceed") || ids.find((id) => outcomes[id].limit) || "out_green";
  }

  get isEducational() {
    return this.mode === "educational";
  }

  render(id, isBack) {
    // Educational quiz: no personal information is collected, so any form screen (the name and
    // email capture, the record form) is skipped before it touches history or posts a step.
    const hop = this.educationalHopTarget(id);
    if (hop) {
      this.render(hop, isBack);
      return;
    }
    const from = this._stepFromOverride || this.current;
    const answer = this._stepAnswer;
    this._stepFromOverride = null;
    this._stepAnswer = null;
    if (!isBack && this.current) this.history.push(this.current);
    this.current = id;
    // Back re-renders a screen already in the path: not a step. Everything else is a forward transition.
    if (!isBack) this.postStep(from, answer, id);

    this.root.classList.remove("mdi-intake__card--fade");
    void this.root.offsetWidth;
    this.root.classList.add("mdi-intake__card--fade");
    this.scrollToTop();

    const outcome = this.config.outcomes && this.config.outcomes[id];
    if (outcome) {
      this.clearProgress();
      this.setStatusAndProgress("outcome", id);
      this.renderOutcome(id, outcome);
      // Include the captured contact so a disqualified / non-passing outcome can still be followed up.
      this.dispatch("outcome", { id, code: outcome.code, contact: this.leadCaptured });
      // Route the triage result to the CRM (Iterable / Zoho) via the backend. The theme never holds
      // CRM credentials; it posts the triage event to a same-origin endpoint that maps code -> journey.
      this.routeOutcome(id, outcome);
      return;
    }

    const screen = this.config.screens[id];
    if (!screen) {
      console.error("[mdi-intake] unknown screen:", id);
      return;
    }

    if (screen.type === "form") {
      this.setStatusAndProgress("form", id);
      this.renderFormScreen(screen);
    } else if (screen.type === "message") {
      this.setStatusAndProgress("message", id);
      this.renderMessage(screen);
    } else if (screen.type === "checklist") {
      this.setStatusAndProgress("question", id);
      this.renderChecklist(screen);
    } else {
      this.setStatusAndProgress("question", id);
      this.renderSingle(screen);
    }

    this.dispatch("screen", { id, type: screen.type });
    this.saveProgress();
  }

  goBack() {
    if (!this.history.length) return;
    const prev = this.history.pop();
    this.render(prev, true);
  }

  // Record the answer to the question currently on screen (this.current). Re-answering a question
  // (via Back or the review "Edit") drops every later answer so the trail and flags never go stale.
  // flag is the single answer-level flag; extraFlags are per-item flags (checklists) kept apart so a
  // later "Proceed anyway" override on flagByAnswer never drops them.
  recordAnswer(screen, answerLabel, flag, extraFlags = []) {
    const id = this.current;
    if (!id) return;
    const idx = this.answerOrder.indexOf(id);
    if (idx !== -1) {
      this.answerOrder.splice(idx + 1).forEach((later) => {
        this.answers.delete(later);
        this.flagByAnswer.delete(later);
        this.itemFlagsByAnswer.delete(later);
      });
    } else {
      this.answerOrder.push(id);
    }
    this.answers.set(id, {
      id,
      question: screen.question,
      answerLabel,
      // Kept for the MDI case: the prescriber sees every question with what was offered and chosen.
      description: screen.sub || null,
      options: this.displayedOptions(screen),
    });
    this.flagByAnswer.set(id, flag || null);
    this.itemFlagsByAnswer.set(id, [...(extraFlags || [])]);
    this._stepAnswer = answerLabel;
    this.recomputeFlags();
  }

  // The choices the shopper saw on a question screen, as labels: single-choice options or checklist
  // items. Empty for anything else.
  displayedOptions(screen) {
    const source = screen.type === "checklist" ? screen.items : screen.options;
    return (source || []).map((o) => o.label).filter(Boolean);
  }

  // The questionnaire trail in path order, one entry per answered question, for the orchestrator to
  // forward as MDI case questions. Flags are read live from flagByAnswer so a "Proceed anyway"
  // override (which flags the answer after the fact) is included.
  buildAnswers() {
    const out = [];
    this.answerOrder.forEach((id) => {
      const a = this.answers.get(id);
      if (!a) return;
      out.push({
        id,
        question: a.question,
        answer: a.answerLabel,
        description: a.description || null,
        options: a.options || [],
        flag: this.flagByAnswer.get(id) || (this.itemFlagsByAnswer.get(id) || [])[0] || null,
      });
    });
    return out;
  }

  recomputeFlags() {
    const seen = [];
    this.answerOrder.forEach((id) => {
      const all = [this.flagByAnswer.get(id), ...(this.itemFlagsByAnswer.get(id) || [])];
      all.forEach((flag) => {
        if (flag && !seen.includes(flag)) seen.push(flag);
      });
    });
    this.flags = seen;
  }

  checklistAnswerLabel(screen, chosen) {
    if (!chosen.length) return "None selected";
    const byId = {};
    (screen.items || []).forEach((i) => {
      byId[i.id] = i.label;
    });
    return chosen.map((id) => byId[id] || id).join(", ");
  }

  // Short step answer for the funnel: selected items' short labels (or labels) joined by " | ".
  checklistStepAnswer(screen, chosen) {
    if (!chosen.length) return "None selected";
    const byId = {};
    (screen.items || []).forEach((i) => {
      byId[i.id] = i.short || i.label;
    });
    return chosen.map((id) => byId[id] || id).join(" | ");
  }

  // Jump straight back to a specific question from the review screen. Truncates the trail to before
  // that question so re-answering continues cleanly forward.
  editQuestion(id) {
    const idx = this.answerOrder.indexOf(id);
    if (idx === -1) return;
    this.history = this.answerOrder.slice(0, idx);
    this.current = null;
    this.render(id, true);
  }

  // Jump back to a form (the capture or the record) to fix a detail, then return to the review.
  editDetails(ownerId) {
    const target = ownerId || this.reviewFormId;
    if (!target) return;
    this.editReturn = this.reviewFormId;
    this.history = this.answerOrder.slice();
    this.current = null;
    this.render(target, true);
  }

  scrollToTop() {
    // Bring the top of the form into view on every navigation. Guarded for non-browser / test envs.
    // Deferred to the next frame so it runs after render() has swapped the new screen in: Safari
    // cancels a smooth scroll that is in flight when the document shrinks under it, which left the
    // page clamped at the footer whenever a shorter screen followed a tall one (iOS, 2026-10-23).
    if (typeof window === "undefined" || typeof window.scrollTo !== "function") return;
    if (typeof this.getBoundingClientRect !== "function") return;
    const nextFrame = typeof window.requestAnimationFrame === "function" ? window.requestAnimationFrame.bind(window) : (fn) => setTimeout(fn, 0);
    nextFrame(() => {
      const top = this.getBoundingClientRect().top + (window.pageYOffset || 0) - 24;
      try {
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      } catch (_e) {
        window.scrollTo(0, Math.max(0, top));
      }
    });
  }

  /* ---------------- Status counter + progress ---------------- */

  isQuestion(id) {
    const s = this.config.screens[id];
    return !!s && (s.type === "single" || s.type === "checklist");
  }

  successorsOf(id) {
    const s = this.config.screens[id];
    if (!s) return [];
    if (s.type === "single") return (s.options || []).map((o) => o.go);
    if (s.type === "checklist") return (s.logic || []).map((r) => r.go);
    if (s.type === "form") return [s.onComplete].filter(Boolean);
    if (s.type === "message") return [s.go].filter(Boolean);
    return [];
  }

  // Longest number of question screens reachable from `id` (inclusive), following any branch until
  // a form or outcome ends the question run. Used to size the "of N" total. The graph has no forward
  // cycles; the memo both speeds this up and guards against accidental revisits.
  longestQuestionPath(id, memo) {
    if (memo.has(id)) return memo.get(id);
    if (!this.isQuestion(id)) {
      memo.set(id, 0);
      return 0;
    }
    memo.set(id, 1); // provisional, prevents infinite recursion if a cycle is ever introduced
    let best = 0;
    this.successorsOf(id).forEach((succ) => {
      best = Math.max(best, this.longestQuestionPath(succ, memo));
    });
    const val = 1 + best;
    memo.set(id, val);
    return val;
  }

  // "Question X of Y (given your current answers)": X counts the question screens already answered
  // plus this one; Y adds the longest remaining question run from here, so Y shrinks as answers prune
  // branches. The form, review, and outcome screens are stages, not questions, so they show a label
  // instead of a number but still advance the bar.
  setStatusAndProgress(kind, id) {
    const answered = this.history.filter((h) => this.isQuestion(h)).length;
    let text = "";
    let pct = 100;

    if (kind === "question") {
      const index = answered + 1;
      const total = answered + this.longestQuestionPath(id, new Map());
      text = `Question ${index} of ${total}`;
      pct = (index / (total + 1)) * 100;
    } else if (kind === "form") {
      const s = this.config.screens[id];
      text = (s && s.statusLabel) || "Your details";
      pct = (answered / (answered + 1)) * 100;
    } else if (kind === "review") {
      text = "Review";
      pct = ((answered + 0.5) / (answered + 1)) * 100;
    } else if (kind === "message") {
      const s = this.config.screens[id];
      text = (s && s.statusLabel) || "";
      pct = (answered / (answered + 1)) * 100;
    }

    if (this.statusEl) {
      this.statusEl.textContent = text;
      this.statusEl.hidden = !text;
    }
    pct = Math.min(100, Math.max(0, pct));
    if (this.progressFill) this.progressFill.style.width = pct + "%";
    if (this.progressBar) this.progressBar.setAttribute("aria-valuenow", Math.round(pct));
  }

  /* ---------------- Screen headers ---------------- */

  renderHeader(screen) {
    const frag = document.createDocumentFragment();
    // The dynamic "Question X of Y" counter is the single source of truth for position, so the header
    // shows only the section label, no hardcoded step number to drift out of sync.
    if (screen.eyebrow) frag.appendChild(this.el("div", { class: "mdi-intake__eyebrow", text: screen.eyebrow }));
    if (screen.question) frag.appendChild(this.el("h2", { class: "mdi-intake__question", text: screen.question }));
    if (screen.sub) frag.appendChild(this.el("p", { class: "mdi-intake__sub", text: screen.sub }));
    if (screen.analogy) frag.appendChild(this.el("div", { class: "mdi-intake__analogy", text: screen.analogy }));
    return frag;
  }

  /* ---------------- Message / interstitial ---------------- */

  // A non-question screen: a short message with a single Continue button. Used for the "good fit"
  // screen shown after screening passes and before the record form.
  renderMessage(screen) {
    this.root.innerHTML = "";
    if (screen.icon) this.root.appendChild(this.el("div", { class: "mdi-intake__outcome-icon", text: screen.icon }));
    this.root.appendChild(this.renderHeader(this.messageCopy(screen)));

    const cta = this.el("div", { class: "mdi-intake__cta" });
    const btn = this.el("button", {
      class: "mdi-intake__btn mdi-intake__btn--block",
      text: screen.continueLabel || "Continue",
      attrs: { type: "button" },
    });
    btn.addEventListener("click", () => this.render(screen.go));
    cta.appendChild(btn);
    this.root.appendChild(cta);
    // No "Print page" here: a green result carries nothing the shopper needs to hand a provider.
    this.root.appendChild(this.backRow());
  }

  // A message screen reached with prescriber flags in play (an amber note such as dryness or
  // infection, or a proceed_* red override) must not promise a fit: the clinician decides. Each header
  // line has an optional *Flagged variant (eyebrowFlagged, questionFlagged, subFlagged) used when any
  // flag is set; a line without a variant keeps its clean copy. Same rule as the green outcome's
  // leadClean / leadFlagged.
  messageCopy(screen) {
    if (!this.flags.length) return screen;
    return Object.assign({}, screen, {
      eyebrow: screen.eyebrowFlagged !== undefined ? screen.eyebrowFlagged : screen.eyebrow,
      question: screen.questionFlagged !== undefined ? screen.questionFlagged : screen.question,
      sub: screen.subFlagged !== undefined ? screen.subFlagged : screen.sub,
    });
  }

  /* ---------------- Single choice ---------------- */

  renderSingle(screen) {
    this.root.innerHTML = "";
    this.root.appendChild(this.renderHeader(screen));

    const opts = this.el("div", { class: "mdi-intake__options" });
    (screen.options || []).forEach((option) => {
      const btn = this.el("button", { class: "mdi-intake__option", attrs: { type: "button" } });
      btn.appendChild(this.el("span", { class: "mdi-intake__option-label", text: option.label }));
      if (option.hint) btn.appendChild(this.el("span", { class: "mdi-intake__option-hint", text: option.hint }));
      btn.addEventListener("click", () => {
        this.recordAnswer(screen, option.label, option.flag || null);
        this.render(option.go);
      });
      opts.appendChild(btn);
    });
    this.root.appendChild(opts);
    this.root.appendChild(this.backRow());
  }

  /* ---------------- Checklist ---------------- */

  renderChecklist(screen) {
    this.root.innerHTML = "";
    this.root.appendChild(this.renderHeader(screen));

    const selected = new Set();
    const exclusive = (screen.items || []).find((i) => i.exclusive);
    const exclusiveId = exclusive ? exclusive.id : null;

    const opts = this.el("div", { class: "mdi-intake__options" });
    const rows = [];

    (screen.items || []).forEach((item) => {
      const row = this.el("button", {
        class: "mdi-intake__check",
        attrs: { type: "button", role: "checkbox", "aria-checked": "false", "data-id": item.id },
      });
      row.appendChild(this.el("span", { class: "mdi-intake__check-box" }));

      const textWrap = this.el("span", { class: "mdi-intake__check-text" });
      textWrap.appendChild(this.el("span", { text: item.label }));
      if (item.hint) textWrap.appendChild(this.el("span", { class: "mdi-intake__check-hint", text: item.hint }));
      row.appendChild(textWrap);

      row.addEventListener("click", () => {
        const isExclusive = item.id === exclusiveId;
        if (isExclusive) {
          selected.clear();
          selected.add(item.id);
        } else {
          if (exclusiveId) selected.delete(exclusiveId);
          if (selected.has(item.id)) selected.delete(item.id);
          else selected.add(item.id);
        }
        rows.forEach((r) => {
          const on = selected.has(r.dataset.id);
          r.classList.toggle("mdi-intake__check--on", on);
          r.setAttribute("aria-checked", on ? "true" : "false");
        });
        submit.disabled = selected.size === 0;
      });

      rows.push(row);
      opts.appendChild(row);
    });
    this.root.appendChild(opts);

    const cta = this.el("div", { class: "mdi-intake__cta" });
    const submit = this.el("button", {
      class: "mdi-intake__btn",
      text: screen.submitLabel || "Continue",
      attrs: { type: "button", disabled: "" },
    });
    submit.addEventListener("click", () => this.submitChecklist(screen, [...selected], exclusiveId));
    cta.appendChild(submit);
    this.root.appendChild(cta);
    this.root.appendChild(this.backRow());
  }

  // Continue handler of a checklist screen: route, record the answer and its item flags, advance.
  submitChecklist(screen, chosen, exclusiveId) {
    const target = this.resolveChecklist(screen, chosen, exclusiveId);
    if (!target) return;
    const itemFlags = chosen
      .map((id) => ((screen.items || []).find((i) => i.id === id) || {}).flag)
      .filter(Boolean);
    this.recordAnswer(screen, this.checklistAnswerLabel(screen, chosen), null, itemFlags);
    this._stepAnswer = this.checklistStepAnswer(screen, chosen);
    this.render(target);
  }

  // Evaluate the checklist logic rules top to bottom, first match wins. The exclusive ("none")
  // item is always treated as an implicit exclusion for any_except so a lone "none" never routes
  // to a medical outcome.
  resolveChecklist(screen, selected, exclusiveId) {
    const rules = screen.logic || [];
    for (const rule of rules) {
      if (!rule.when) return rule.go; // default / catch-all
      if (rule.when === "none_or_empty") {
        if (selected.length === 0 || (exclusiveId && selected.includes(exclusiveId))) return rule.go;
      } else if (rule.when === "only") {
        const want = [...(rule.ids || [])].sort().join(",");
        const have = [...selected].sort().join(",");
        if (want === have) return rule.go;
      } else if (rule.when === "any") {
        const want = new Set(rule.ids || []);
        if (selected.some((id) => want.has(id))) return rule.go;
      } else if (rule.when === "any_except") {
        const excluded = new Set([...(rule.ids || []), exclusiveId].filter(Boolean));
        if (selected.some((id) => !excluded.has(id))) return rule.go;
      }
    }
    return null;
  }

  /* ---------------- Patient-record form ---------------- */

  renderFormScreen(screen) {
    this.root.innerHTML = "";
    this.root.appendChild(this.renderHeader(screen));

    const grid = this.el("div", { class: "mdi-intake__form" });
    const controls = {};

    (screen.fields || []).forEach((field) => {
      const wrap = this.el("div", {
        class: "mdi-intake__field" + (field.span ? " mdi-intake__field--span" : ""),
        attrs: { "data-field": field.id },
      });

      const head = this.el("div", { class: "mdi-intake__field-head" });
      const label = this.el("label", { attrs: { for: this.uid(field.id) } });
      label.appendChild(document.createTextNode(field.label));
      if (field.required) label.appendChild(this.el("span", { class: "mdi-intake__req", text: " *" }));
      head.appendChild(label);
      wrap.appendChild(head);

      let control;
      if (field.type === "select") {
        control = this.el("select", { attrs: { id: this.uid(field.id) } });
        control.appendChild(this.el("option", { text: "Select...", attrs: { value: "", disabled: "", selected: "" } }));
        (field.options || []).forEach((o) => {
          control.appendChild(this.el("option", { text: o.label, attrs: { value: o.value } }));
        });
      } else {
        control = this.el("input", { attrs: { id: this.uid(field.id), type: field.type || "text", autocomplete: "off" } });
        if (field.max) control.setAttribute("maxlength", String(field.max));
        if (field.type === "email") control.setAttribute("inputmode", "email");
        if (field.type === "tel") control.setAttribute("inputmode", "tel");
        // Enter submits, as it would in a native <form>. The fields are not wrapped in one, so the
        // keypress is wired here; validation runs exactly as it does for the button.
        control.addEventListener("keydown", (e) => {
          if (e.key !== "Enter" || e.isComposing) return;
          e.preventDefault();
          this.submitForm(screen, controls);
        });
      }
      if (this.values[field.id] != null) control.value = this.values[field.id];
      control.addEventListener("input", () => {
        this.clearFieldError(field.id);
        this.noteFieldInput(field, control);
      });
      control.addEventListener("change", () => {
        this.clearFieldError(field.id);
        this.noteFieldInput(field, control);
      });
      wrap.appendChild(control);
      controls[field.id] = control;

      if (field.hint) wrap.appendChild(this.el("span", { class: "mdi-intake__field-hint", text: field.hint }));
      wrap.appendChild(this.el("span", { class: "mdi-intake__field-error", attrs: { "data-error-for": field.id } }));
      grid.appendChild(wrap);
    });
    this.root.appendChild(grid);

    this.setupAddressBlock(screen, controls, grid);

    const cta = this.el("div", { class: "mdi-intake__cta" });
    const submit = this.el("button", {
      class: "mdi-intake__btn mdi-intake__btn--block",
      text: screen.submitLabel || "Finish",
      attrs: { type: "button" },
    });
    submit.addEventListener("click", () => this.submitForm(screen, controls));
    cta.appendChild(submit);
    this.root.appendChild(cta);

    if (screen.privacy) {
      this.root.appendChild(this.el("p", { class: "mdi-intake__privacy", text: "🔒 " + screen.privacy }));
    }
    this.root.appendChild(this.backRow());
  }

  submitForm(screen, controls) {
    let ok = true;
    let firstBad = null;

    // Finishing while the address finder is still up: show the real fields so any error lands
    // on an input the shopper can see and type into.
    this.showAddressFields();

    (screen.fields || []).forEach((field) => {
      const control = controls[field.id];
      const value = (control && control.value ? control.value : "").trim();
      this.values[field.id] = value;

      const message = this.validateField(field, value);
      if (message) {
        ok = false;
        this.setFieldError(field.id, message);
        if (!firstBad) firstBad = field.id;
      }
    });

    if (!ok) {
      const bad = this.root.querySelector(`[data-field="${firstBad}"]`);
      if (bad && typeof bad.scrollIntoView === "function") {
        bad.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }

    // Up-front capture hands the contact off early (light: name + email, no confirm step).
    if (screen.lead) this.emitLead(screen);

    // If the user came here from the review to fix something, take them straight back to the review.
    if (this.editReturn) {
      const back = this.editReturn;
      this.editReturn = null;
      this.renderReview(this.config.screens[back]);
      return;
    }

    // The record form carries a review config: confirm before creating the record. Any other form
    // (the up-front capture) simply advances into the flow.
    if (screen.review) {
      this.renderReview(screen);
      return;
    }
    this.render(screen.onComplete);
  }

  finishSubmit(screen) {
    const payload = this.buildPayload();
    this._submitFailureShown = false;
    this._lastPayload = payload; // kept for the CRM routing call on the outcome screen
    this.dispatch("complete", { payload });
    this.sendToOrchestrator(payload);
    // The review screen is an internal sentinel (__review__); the step the report sees leaves the
    // record form, which is the last real screen in the path.
    this._stepFromOverride = this.reviewFormId || this.current;
    this.render(screen.onComplete || "out_green");
  }

  // Hand the triage result to the backend so it can enroll the contact in the right CRM journey
  // (Iterable today, Zoho next). Deliberately credential-free: the theme posts a same-origin event
  // and the backend maps triage code -> journey and holds the CRM secret. See docs for the App Proxy
  // security model. Fires once per outcome, non-blocking.
  routeOutcome(id, outcome) {
    const event = {
      outcome: id,
      // The orchestrator maps green / amber / red to a CRM journey, so triageCode carries the
      // outcome's code, not its id. outcomeCode is kept for anything already reading it.
      triageCode: outcome.code || null,
      outcomeCode: outcome.code || null,
      passed: !!outcome.green,
      // Clinically green but blocked from purchase by the rolling 12-month one-per-customer limit, so
      // the backend can enroll them in a "come back later / refills" journey instead of a purchase one.
      starterKitLimited: !!(outcome.green && this.starterKitIneligible),
      market: this.market,
      mode: this.mode,
      contact: this.leadCaptured || null,
      record: outcome.green ? this._lastPayload || null : null,
      brand: (this.config.meta && this.config.meta.brand) || null,
      sessionId: this.sessionId,
    };
    this.dispatch("route", event);

    // The educational quiz holds no contact and reports to nobody: no CRM routing post.
    if (this.isEducational) return;
    if (!this.routingUrl || this._routed) return;
    this._routed = true;
    this.postJson(this.routingUrl, event, "routing");
  }

  // Light email capture at the start. Keeps the contact locally (it rides along on the routing and
  // submit payloads) and fires an event. It does NOT post to lead_url: that hand-off is deferred to
  // "Print page" (see postLead), so simply starting the intake never enrolls anyone in the lead
  // (intake-abandon) journey. De-duped so editing does not re-dispatch an unchanged contact.
  emitLead(screen) {
    const lead = {};
    const fields = screen.fields || [];
    // A merged form marks its contact fields with lead: true; only those are the lead. Legacy
    // configs with no lead-flagged field keep the old behaviour (every field on the screen).
    const contact = fields.some((f) => f.lead === true) ? fields.filter((f) => f.lead === true) : fields;
    contact.forEach((f) => {
      const v = this.values[f.id];
      if (v) lead[f.mdiPath || f.id] = v;
    });
    this.leadCaptured = lead;
    const serialized = JSON.stringify(lead);
    if (serialized === this._lastLead) return;
    this._lastLead = serialized;
    this.dispatch("lead", { lead });
  }

  // Hands the captured contact to lead_url. Called from printPage(), so it fires only when the shopper
  // clicks "Print page" on an outcome (a deliberate "I'm leaving with this" signal), not on intake
  // start. Fire-and-forget; de-duped so printing twice does not re-post an unchanged contact.
  postLead() {
    if (!this.leadUrl || !this.leadCaptured) return;
    const serialized = JSON.stringify(this.leadCaptured);
    if (serialized === this._lastLeadPosted) return;
    this._lastLeadPosted = serialized;
    this.postJson(this.leadUrl, this.leadCaptured, "lead");
  }

  // Per-run id for the funnel rows. crypto.randomUUID where available; the fallback shape is what
  // the orchestrator's sessionIdOf() accepts.
  newSessionId() {
    try {
      if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    } catch (e) {
      /* fall through */
    }
    return "s-" + Date.now().toString(16) + "-" + Math.random().toString(16).slice(2, 10);
  }

  // Fire-and-forget same-origin JSON post. Every intake hand-off (step, routing, lead, keep in touch)
  // goes through here: keepalive so a navigation does not cancel it, and a warning instead of a throw
  // so no post can ever break the screen the shopper is on.
  postJson(url, body, label) {
    try {
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        keepalive: true,
        body: JSON.stringify(body),
      }).catch((err) => console.warn("[mdi-intake] " + label + " post failed", err));
    } catch (err) {
      console.warn("[mdi-intake] " + label + " post threw", err);
    }
  }

  // One forward screen transition: the screen left (null on the first screen), the option label chosen
  // there (null on forms and messages), the screen entered. Dispatched for analytics and posted to
  // event_url when configured.
  postStep(from, answer, to) {
    const detail = { sessionId: this.sessionId, from: from || null, answer: answer || null, to, market: this.market, mode: this.mode };
    this.dispatch("step", detail);
    if (!this.eventUrl || !this.sessionId) return;
    this.postJson(this.eventUrl, Object.assign({ eventType: "step" }, detail, { brand: (this.config.meta && this.config.meta.brand) || null }), "step");
  }

  // Every field across every form screen, in document order, with the id of the form that owns it.
  allFormFields() {
    const out = [];
    Object.entries(this.config.screens || {}).forEach(([id, s]) => {
      if (s.type === "form") (s.fields || []).forEach((field) => out.push({ field, ownerId: id }));
    });
    return out;
  }

  /* ---------------- Review step ---------------- */

  renderReview(formScreen) {
    // Enter a synthetic "review" state. Pushing the form id lets Back / Edit return to the form with
    // every value preserved (values live in this.values and the form repopulates from them).
    this.history.push(this.current);
    this.current = "__review__";
    this.setStatusAndProgress("review", "__review__");

    this.root.classList.remove("mdi-intake__card--fade");
    void this.root.offsetWidth;
    this.root.classList.add("mdi-intake__card--fade");
    this.root.innerHTML = "";

    const review = formScreen.review || {};
    this.root.appendChild(
      this.renderHeader({
        eyebrow: review.eyebrow || "Review",
        question: review.question || "Does everything look right?",
        sub: review.sub || "Check your answers and details before we create your record. You can edit anything.",
      })
    );

    // Section 1: questionnaire answers. Each row jumps straight back to that question.
    if (this.answerOrder.length) {
      this.root.appendChild(this.el("div", { class: "mdi-intake__review-title", text: review.answersTitle || "Your answers" }));
      const answersList = this.el("div", { class: "mdi-intake__review-list" });
      this.answerOrder.forEach((id) => {
        const answer = this.answers.get(id);
        if (!answer) return;
        answersList.appendChild(
          this.reviewRow(answer.question, answer.answerLabel, () => this.editQuestion(id))
        );
      });
      this.root.appendChild(answersList);
    }

    // Section 2: personal details, gathered across every form screen (up-front capture + record).
    // Edit jumps back to the specific form that owns that field.
    this.root.appendChild(this.el("div", { class: "mdi-intake__review-title", text: review.detailsTitle || "Your details" }));
    const detailsList = this.el("div", { class: "mdi-intake__review-list" });
    this.allFormFields().forEach(({ field, ownerId }) => {
      const value = this.values[field.id];
      if (value == null || value === "") return;
      detailsList.appendChild(
        this.reviewRow(field.label, this.displayValue(field, value), () => this.editDetails(ownerId))
      );
    });
    this.root.appendChild(detailsList);

    const cta = this.el("div", { class: "mdi-intake__cta" });
    const submit = this.el("button", {
      class: "mdi-intake__btn mdi-intake__btn--block",
      text: review.submitLabel || "Submit Questionnaire",
      attrs: { type: "button" },
    });
    submit.addEventListener("click", () => this.finishSubmit(formScreen));
    cta.appendChild(submit);
    this.root.appendChild(cta);

    if (formScreen.privacy) {
      this.root.appendChild(this.el("p", { class: "mdi-intake__privacy", text: "🔒 " + formScreen.privacy }));
    }

    const backRow = this.el("div", { class: "mdi-intake__backrow" });
    const edit = this.el("button", { class: "mdi-intake__back", text: "← " + (review.editLabel || "Edit my details"), attrs: { type: "button" } });
    edit.addEventListener("click", () => this.editDetails());
    backRow.appendChild(edit);
    this.root.appendChild(backRow);
  }

  reviewRow(key, value, onEdit) {
    const row = this.el("div", { class: "mdi-intake__review-row" });
    const main = this.el("div", { class: "mdi-intake__review-main" });
    main.appendChild(this.el("span", { class: "mdi-intake__review-key", text: key }));
    main.appendChild(this.el("span", { class: "mdi-intake__review-val", text: value }));
    row.appendChild(main);
    const edit = this.el("button", { class: "mdi-intake__review-edit", text: "Edit", attrs: { type: "button" } });
    edit.addEventListener("click", onEdit);
    row.appendChild(edit);
    return row;
  }

  // For selects, show the human option label rather than the raw code that is sent to MDI.
  displayValue(field, value) {
    if (field.type === "select" && Array.isArray(field.options)) {
      const match = field.options.find((o) => String(o.value) === String(value));
      if (match) return match.label;
    }
    return String(value);
  }

  // Assemble the MDI patient record from the collected values. mdiPath drives the shape (dotted
  // paths nest, e.g. "address.zip_code"), and valueType coerces select codes to integers so the
  // payload is ready to hand to the orchestrator / MDI without a second mapping pass.
  buildPayload() {
    const patient = {};
    this.allFormFields().forEach(({ field }) => {
      let value = this.values[field.id];
      if (value == null || value === "") return;
      if (field.valueType === "int") value = parseInt(value, 10);
      this.setPath(patient, field.mdiPath || field.id, value);
    });

    return {
      patient,
      answers: this.buildAnswers(),
      flags: this.flags.slice(),
      notes: this.flags.map((f) => (this.config.notes && this.config.notes[f]) || f),
      triageCode: this.recordTriageCode(),
      brand: (this.config.meta && this.config.meta.brand) || null,
      customerId: this.customerId,
      market: this.market,
      sessionId: this.sessionId,
    };
  }

  // Triage code for the record the prescriber sees. Reaching the record form means screening passed,
  // but the flags say how: a proceed_* flag is a red screen the shopper overrode ("Proceed anyway"),
  // any other flag is an amber prescriber note (dryness, infection), and no flags
  // is a clean green. Distinct from the CRM routing code (routeOutcome), which stays the outcome's own
  // code because the shopper did continue to purchase.
  recordTriageCode() {
    if (this.flags.some((f) => String(f).indexOf("proceed_") === 0)) return "red";
    if (this.flags.length) return "amber";
    return "green";
  }

  setPath(target, path, value) {
    const parts = String(path).split(".");
    let node = target;
    for (let i = 0; i < parts.length - 1; i += 1) {
      node[parts[i]] = node[parts[i]] || {};
      node = node[parts[i]];
    }
    node[parts[parts.length - 1]] = value;
  }

  // Orchestrator submission. On a green pass the assembled patient record is POSTed here and the
  // checkout handoff (proceed) waits for the answer, so a shopper never reaches checkout with no MDI
  // patient behind the order unless they explicitly chose to. The orchestrator always answers 200
  // and reports `record`: "created" / "existing" (patient in place) or "skipped" (nothing needed)
  // let proceed() continue; "failed", a non-2xx, a timeout, or a network error gets ONE automatic
  // retry, after which renderSubmitFailure shows the error and asks the shopper to confirm before
  // continuing without a record. With no submit_url (or an educational market) there is nothing
  // to send and the gate resolves immediately. Resolves, never rejects.
  sendToOrchestrator(payload) {
    if (!this.submitUrl || this.isEducational) {
      this._submit = Promise.resolve({ ok: true, record: "skipped" });
      return this._submit;
    }
    this._submit = this.postRecord(payload, 1);
    return this._submit;
  }

  async postRecord(payload, retries) {
    let last = { ok: false, reason: "unknown", attempt: 0 };
    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = typeof AbortController === "function" ? new AbortController() : null;
      const timer = controller ? setTimeout(() => controller.abort(), 20000) : null;
      try {
        const res = await fetch(this.submitUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
          signal: controller ? controller.signal : undefined,
        });
        const data = res.ok ? await res.json().catch(() => ({})) : null;
        // Only a record the orchestrator confirmed may reach checkout: "created" / "existing" (MDI
        // patient in place) or "skipped" (nothing needed, e.g. educational market). Anything else,
        // including any MDI error ("failed", with MDI's message) or an unknown status, blocks.
        const record = data && data.ok !== false ? data.record : null;
        if (res.ok && (record === "created" || record === "existing" || record === "skipped")) {
          this.intakeToken = (data && typeof data.token === "string" && data.token) || null;
          return { ok: true, record, token: this.intakeToken, attempt };
        }
        last = {
          ok: false,
          reason: res.ok ? (data && data.reason) || "record_failed" : "http_" + res.status,
          message: (res.ok && data && data.message) || null,
          retryable: res.ok && data ? data.retryable !== false : true,
          attempt,
        };
      } catch (err) {
        last = { ok: false, reason: err && err.name === "AbortError" ? "timeout" : "network", attempt };
      } finally {
        if (timer) clearTimeout(timer);
      }
      console.warn("[mdi-intake] submit attempt failed", last);
      if (attempt < retries) await this.delay(1000);
    }
    this.dispatch("submit-failed", last);
    return last;
  }

  /* ---------------- Address block: search, then confirm ---------------- */

  // The postal-address fields of a form screen, by their MDI path ("address.*").
  addressFields(screen) {
    return (screen.fields || []).filter((f) => f.mdiPath && f.mdiPath.indexOf("address.") === 0);
  }

  addressCopy(screen) {
    return Object.assign(
      {
        finderHint: "Start typing and choose your address from the list.",
        manualLabel: "Enter my address manually",
        searchAgainLabel: "Search again",
      },
      screen.addressCopy || {},
    );
  }

  // Search, then confirm. With a Places key the address starts as one finder (our own input fed by
  // Places Autocomplete Data, US only) standing in for the address fields. Picking a suggestion
  // fills the real street / apt / city / state / ZIP inputs and reveals them, so what the shopper
  // sees is exactly what is submitted and anything can be corrected; "Enter my address manually"
  // skips the finder (and carries the typed text into the street field). The finder only appears
  // while every address value is empty: editing from the review, a blocked Google script, an older
  // key, or no key at all land straight on the fields, which always work on their own.
  //
  // Why not keep the finder as the field: its typed text never counted (only a picked suggestion
  // did), street-level suggestions dropped the house number, Escape wiped the text, and there was
  // nowhere for an apartment. Order #V6130G shipped to "42nd Avenue Northeast".
  //
  // Why not Google's PlaceAutocompleteElement (used 2026-10-08 to 2026-10-23): on viewports under
  // about 600px it switches to a full-screen search sheet on the first keystroke, inside a closed
  // shadow root with no opt-out. With the deliverable-address type filter the sheet was often empty,
  // so on a phone the page "whited out"; a swipe-back to escape it left the intake and lost the
  // form. The suggestions now render inline under the input, in the page, like any other dropdown.
  setupAddressBlock(screen, controls, grid) {
    this.addressFinder = null;
    const fields = this.addressFields(screen);
    const street = fields.find((f) => f.mdiPath === "address.address");
    if (!street || !controls[street.id] || !this.placesEnabled) return;
    const byPath = {};
    fields.forEach((f) => {
      if (controls[f.id]) byPath[f.mdiPath] = controls[f.id];
    });

    this.whenGoogleReady(async () => {
      if (!grid.isConnected) return; // the shopper moved on while the script loaded
      let places;
      try {
        places = await window.google.maps.importLibrary("places");
      } catch (err) {
        console.warn("[mdi-intake] places library failed to load", err);
        return;
      }
      if (!grid.isConnected) return;

      if (places.AutocompleteSuggestion && typeof places.AutocompleteSuggestion.fetchAutocompleteSuggestions === "function") {
        const untouched = fields.every((f) => !(controls[f.id].value || "").trim() && document.activeElement !== controls[f.id]);
        if (untouched) this.showAddressFinder(screen, controls, grid, byPath, places);
        else this.addSearchAgain(screen, controls, grid, byPath, places);
        return;
      }

      if (places.Autocomplete) {
        // Legacy Places (older keys): attaches to the street input itself; the fields stay visible.
        const ac = new places.Autocomplete(controls[street.id], {
          types: ["address"],
          fields: ["address_components"],
          componentRestrictions: { country: ["us"] },
        });
        ac.addListener("place_changed", () => this.fillFromPlace(ac.getPlace(), byPath, screen));
      }
    });
  }

  showAddressFinder(screen, controls, grid, byPath, places) {
    const fields = this.addressFields(screen);
    const street = fields.find((f) => f.mdiPath === "address.address");
    const streetWrap = this.root.querySelector(`[data-field="${street.id}"]`);
    if (!streetWrap || this.addressFinder) return;
    const copy = this.addressCopy(screen);
    const inputId = this.uid("address_finder");
    const listId = this.uid("address_finder_list");

    const wrap = this.el("div", {
      class: "mdi-intake__field mdi-intake__field--span mdi-intake__finder",
      attrs: { "data-address-finder": "" },
    });
    const head = this.el("div", { class: "mdi-intake__field-head" });
    const label = this.el("label", { attrs: { for: inputId } });
    label.appendChild(document.createTextNode(street.label));
    if (street.required) label.appendChild(this.el("span", { class: "mdi-intake__req", text: " *" }));
    head.appendChild(label);
    wrap.appendChild(head);

    // A plain input with a listbox under it. Suggestions come from Places Autocomplete Data
    // (fetchAutocompleteSuggestions) grouped under one session token until a pick, which is what
    // keeps the per-session pricing. No type filter: partial input then always has something to
    // show, and a street-level pick (no house number) is caught by streetAddressProblem on the
    // revealed street field exactly as a typed one is.
    const box = this.el("div", { class: "mdi-intake__suggest" });
    const input = this.el("input", {
      class: "mdi-intake__places",
      attrs: {
        id: inputId,
        type: "text",
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "words",
        spellcheck: "false",
        enterkeyhint: "search",
        placeholder: copy.finderPlaceholder || "",
        "aria-label": street.label,
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-expanded": "false",
        "aria-controls": listId,
      },
    });
    const list = this.el("ul", { class: "mdi-intake__suggest-list", attrs: { id: listId, role: "listbox", hidden: "" } });
    box.appendChild(input);
    box.appendChild(list);
    wrap.appendChild(box);
    wrap.appendChild(this.el("span", { class: "mdi-intake__field-hint", text: copy.finderHint }));

    const manual = this.el("button", { class: "mdi-intake__linkbtn", text: copy.manualLabel, attrs: { type: "button" } });
    manual.addEventListener("click", () => {
      const typed = (input.value || "").trim();
      this.showAddressFields();
      if (typed && !controls[street.id].value) {
        controls[street.id].value = typed;
        this.noteFieldInput(street, controls[street.id]);
      }
      controls[street.id].focus();
    });
    wrap.appendChild(manual);
    wrap.appendChild(this.el("span", { class: "mdi-intake__field-error", attrs: { "data-error-for": "address_finder" } }));

    let sessionToken = places.AutocompleteSessionToken ? new places.AutocompleteSessionToken() : undefined;
    const fetchSuggestions = async (text) => {
      const request = { input: text, includedRegionCodes: ["us"], language: "en-US", region: "us" };
      if (sessionToken) request.sessionToken = sessionToken;
      const result = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
      return ((result && result.suggestions) || [])
        .filter((sug) => sug && sug.placePrediction)
        .map((sug) => Object.assign(MdiIntakeForm.suggestionLabel(sug), { prediction: sug.placePrediction }));
    };

    const onPick = async (item) => {
      try {
        const place = item.prediction.toPlace();
        await place.fetchFields({ fields: ["addressComponents"] });
        // A pick ends the billing session; the next search starts a new one.
        sessionToken = places.AutocompleteSessionToken ? new places.AutocompleteSessionToken() : undefined;
        const hasNumber = this.fillFromPlace(this.normalizeNewPlace(place), byPath, screen);
        this.showAddressFields();
        fields.forEach((f) => this.noteFieldInput(f, controls[f.id]));
        // Confirmed address: move on to the next empty field; a street with no number stays put.
        const next = hasNumber ? fields.map((f) => controls[f.id]).find((c) => c && !c.value) : controls[street.id];
        if (next && typeof next.focus === "function") next.focus({ preventScroll: true });
      } catch (err) {
        console.warn("[mdi-intake] place lookup failed", err);
        this.showAddressFields();
        controls[street.id].focus();
      }
    };

    const suggest = MdiIntakeForm.addressSuggest({
      fetchSuggestions,
      onPick,
      onChange: (state) => {
        list.innerHTML = "";
        state.items.forEach((item, i) => {
          const li = this.el("li", {
            class: "mdi-intake__suggest-item" + (i === state.active ? " mdi-intake__suggest-item--active" : ""),
            attrs: { role: "option", id: `${listId}-${i}`, "aria-selected": i === state.active ? "true" : "false" },
          });
          li.appendChild(this.el("span", { class: "mdi-intake__suggest-main", text: item.main }));
          if (item.secondary) li.appendChild(this.el("span", { class: "mdi-intake__suggest-secondary", text: item.secondary }));
          // pointerdown (not click) so the pick lands before the input's blur closes the list.
          li.addEventListener("pointerdown", (e) => {
            e.preventDefault();
            suggest.pick(i);
          });
          list.appendChild(li);
        });
        list.hidden = !state.open;
        input.setAttribute("aria-expanded", state.open ? "true" : "false");
        if (state.open && state.active >= 0) input.setAttribute("aria-activedescendant", `${listId}-${state.active}`);
        else input.removeAttribute("aria-activedescendant");
      },
    });
    input.addEventListener("input", () => {
      this.clearFieldError("address_finder");
      suggest.input(input.value);
    });
    input.addEventListener("keydown", (e) => {
      if (e.isComposing) return;
      if (suggest.key(e.key)) e.preventDefault();
      else if (e.key === "Enter") e.preventDefault(); // never submits the form from the finder
    });
    input.addEventListener("focus", () => suggest.reopen());
    input.addEventListener("blur", () => suggest.close());

    fields.forEach((f) => {
      const w = this.root.querySelector(`[data-field="${f.id}"]`);
      if (w) w.hidden = true;
    });
    grid.insertBefore(wrap, streetWrap);
    this.addressFinder = { wrap, screen, controls, grid, byPath, places, input };
  }

  // Query / list state behind the address finder, kept free of the DOM so it can be tested: input()
  // debounces and drops replies to superseded queries, key() handles the listbox keys and reports
  // whether it consumed one, pick() hands the chosen item to onPick. onChange(state) is the render
  // hook. Fewer than minChars characters never query.
  static addressSuggest({ fetchSuggestions, onPick, onChange, minChars = 3, debounceMs = 250 }) {
    const state = { items: [], open: false, active: -1, _seq: 0, _timer: null };
    const notify = () => {
      if (onChange) onChange(state);
    };
    const set = (items, open) => {
      state.items = items;
      state.open = !!open && items.length > 0;
      state.active = -1;
      notify();
    };
    const run = async (text, seq) => {
      let items = [];
      try {
        items = (await fetchSuggestions(text)) || [];
      } catch (err) {
        console.warn("[mdi-intake] address suggestions failed", err);
        items = [];
      }
      if (seq !== state._seq) return; // a newer query is in flight or has landed
      set(items, true);
    };
    state.input = (value) => {
      const text = String(value || "").trim();
      const seq = ++state._seq;
      if (state._timer) {
        clearTimeout(state._timer);
        state._timer = null;
      }
      if (text.length < minChars) {
        set([], false);
        return Promise.resolve();
      }
      if (!debounceMs) return run(text, seq);
      return new Promise((resolve) => {
        state._timer = setTimeout(() => {
          state._timer = null;
          run(text, seq).then(resolve);
        }, debounceMs);
      });
    };
    state.pick = (i) => {
      const item = state.items[i];
      if (!item) return false;
      state.open = false;
      state.active = -1;
      notify();
      onPick(item);
      return true;
    };
    state.close = () => {
      if (!state.open) return;
      state.open = false;
      state.active = -1;
      notify();
    };
    state.reopen = () => {
      if (state.open || !state.items.length) return;
      state.open = true;
      notify();
    };
    state.key = (key) => {
      if (key === "Escape") {
        if (!state.open) return false;
        state.close();
        return true;
      }
      if (!state.open || !state.items.length) return false;
      if (key === "ArrowDown") {
        state.active = (state.active + 1) % state.items.length;
        notify();
        return true;
      }
      if (key === "ArrowUp") {
        state.active = (state.active - 1 + state.items.length) % state.items.length;
        notify();
        return true;
      }
      if (key === "Enter") return state.active >= 0 ? state.pick(state.active) : false;
      return false;
    };
    return state;
  }

  // Display text of a Places (New) suggestion: the prediction's main line and, when there is one,
  // its secondary (locality) line.
  static suggestionLabel(suggestion) {
    const p = (suggestion && suggestion.placePrediction) || suggestion || {};
    const text = (v) => (v && typeof v === "object" ? v.text : v) || "";
    const main = text(p.mainText);
    return { main: String(main || text(p.text)), secondary: String(main ? text(p.secondaryText) : "") };
  }

  // Replace the finder with the address fields (filled or empty). Safe to call when no finder is up.
  showAddressFields() {
    const finder = this.addressFinder;
    if (!finder) return;
    this.addressFinder = null;
    if (finder.wrap.parentNode) finder.wrap.parentNode.removeChild(finder.wrap);
    this.addressFields(finder.screen).forEach((f) => {
      const w = this.root.querySelector(`[data-field="${f.id}"]`);
      if (w) w.hidden = false;
    });
    this.addSearchAgain(finder.screen, finder.controls, finder.grid, finder.byPath, finder.places);
  }

  // "Search again" beside the street label clears the address and brings the finder back.
  addSearchAgain(screen, controls, grid, byPath, places) {
    const fields = this.addressFields(screen);
    const street = fields.find((f) => f.mdiPath === "address.address");
    const wrap = street && this.root.querySelector(`[data-field="${street.id}"]`);
    if (!wrap || wrap.querySelector("[data-address-search-again]")) return;
    const head = wrap.querySelector(".mdi-intake__field-head") || wrap;
    const btn = this.el("button", {
      class: "mdi-intake__linkbtn mdi-intake__linkbtn--small",
      text: this.addressCopy(screen).searchAgainLabel,
      attrs: { type: "button", "data-address-search-again": "" },
    });
    btn.addEventListener("click", () => {
      fields.forEach((f) => {
        if (controls[f.id]) controls[f.id].value = "";
        this.values[f.id] = "";
        this.clearFieldError(f.id);
      });
      this.saveProgress();
      btn.parentNode.removeChild(btn);
      this.showAddressFinder(screen, controls, grid, byPath, places);
      const finder = this.addressFinder;
      if (finder && finder.input && typeof finder.input.focus === "function") finder.input.focus();
    });
    head.appendChild(btn);
  }

  // Places API (New) returns addressComponents as { longText, shortText, types }; map to the
  // legacy { long_name, short_name, types } shape addressPartsFromPlace reads.
  normalizeNewPlace(place) {
    const comps = place.addressComponents || place.address_components || [];
    return {
      address_components: comps.map((c) => ({
        long_name: c.longText != null ? c.longText : c.long_name,
        short_name: c.shortText != null ? c.shortText : c.short_name,
        types: c.types || [],
      })),
    };
  }

  whenGoogleReady(cb, attempts = 0) {
    if (window.google && window.google.maps && typeof window.google.maps.importLibrary === "function") {
      cb();
      return;
    }
    if (attempts > 40) return; // ~10s, then give up quietly and leave the plain fields usable
    setTimeout(() => this.whenGoogleReady(cb, attempts + 1), 250);
  }

  // Address components -> values by MDI path. The street line is house number + the short route
  // name ("1200 42nd Ave NE"): the USPS form, and within MDI's 35-character street limit where the
  // long form ("42nd Avenue Northeast") is not always. hasStreetNumber is false for a street-level
  // place, which the caller surfaces as an error instead of submitting.
  static addressPartsFromPlace(place) {
    const comps = place && place.address_components;
    if (!comps) return { parts: null, hasStreetNumber: false };
    const get = (type, useShort) => {
      const c = comps.find((comp) => (comp.types || []).includes(type));
      if (!c) return "";
      return (useShort ? c.short_name : c.long_name) || "";
    };
    const streetNumber = get("street_number");
    const route = get("route", true);
    return {
      parts: {
        "address.address": [streetNumber, route].filter(Boolean).join(" "),
        "address.address2": get("subpremise"),
        "address.city_name": get("locality") || get("sublocality") || get("postal_town"),
        "address.state_name": get("administrative_area_level_1", true),
        "address.zip_code": get("postal_code"),
      },
      hasStreetNumber: !!streetNumber,
    };
  }

  // Why a street line cannot be shipped to, or null. A deliverable street line starts with a
  // house or building number ("1200 42nd Ave NE", "12B Elm St", "12-14 Harbour Rd") or is a
  // numbered PO Box. An ordinal ("42nd Avenue Northeast") is a street name, not a house number.
  // Mirrors streetAddressProblem in the orchestrator, which rejects the record the same way.
  static isEmail(value) {
    return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(value || "").trim());
  }

  static streetAddressProblem(value) {
    const s = String(value || "").trim();
    if (/^\d+[a-z]?(?:[-/]\d+[a-z]?)?\s+\S/i.test(s)) return null;
    if (/^p\.?\s*o\.?\s*box\s+\d/i.test(s)) return null;
    return "Add your house or building number, for example 1200 Oak Street.";
  }

  // Write a picked place into the fields. Returns whether the place carried a house number.
  fillFromPlace(place, byPath, screen) {
    const { parts, hasStreetNumber } = MdiIntakeForm.addressPartsFromPlace(place);
    if (!parts) return false;
    Object.keys(parts).forEach((path) => {
      const control = byPath[path];
      if (!control) return;
      const field = (screen.fields || []).find((f) => f.mdiPath === path);
      if (!parts[path]) {
        // A fresh pick replaces the whole address; a missing part (no apt) clears the old one.
        if (control.tagName !== "SELECT") control.value = "";
        return;
      }
      // A select (the state list) only takes a value it has an option for; Places returns the
      // USPS code as short_name, which is what the options use. Anything else leaves it unset.
      if (control.tagName === "SELECT" && !Array.from(control.options).some((o) => o.value === parts[path])) return;
      control.value = parts[path];
      if (field) this.clearFieldError(field.id);
    });
    const street = (screen.fields || []).find((f) => f.mdiPath === "address.address");
    if (street && !hasStreetNumber) {
      this.setFieldError(street.id, street.error || MdiIntakeForm.streetAddressProblem(parts["address.address"]));
    }
    return hasStreetNumber;
  }

  /* ---------------- Field validation helpers ---------------- */

  // The message for a field's value, or "" when it is fine. Shared by Finish and the address
  // finder so a problem reads the same wherever it is caught.
  validateField(field, value) {
    if (field.required && !value) return "This field is required.";
    if (!value) return "";
    if (field.minAge && !this.meetsMinAge(value, field.minAge)) return field.error || "Please check this value.";
    // A typo such as 1880 passes the minimum-age check but MDI rejects it as "Invalid date of birth."
    if (field.maxAge && !this.withinMaxAge(value, field.maxAge)) return field.error || "Please check this value.";
    if (field.pattern && !new RegExp(field.pattern).test(value)) return field.error || "Please check this value.";
    if (field.mdiPath === "address.address") {
      const problem = MdiIntakeForm.streetAddressProblem(value);
      if (problem) return field.error || problem;
    }
    return "";
  }

  withinMaxAge(value, maxAge) {
    const parts = String(value).split("-");
    if (parts.length !== 3) return false;
    const y = parseInt(parts[0], 10);
    if (!y) return false;
    return new Date().getFullYear() - y <= maxAge;
  }

  meetsMinAge(value, minAge) {
    const parts = String(value).split("-");
    if (parts.length !== 3) return false;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d) return false;

    const dob = new Date(y, m - 1, d); // local time (avoids UTC parsing surprises)
    const now = new Date();
    if (isNaN(dob.getTime()) || dob > now) return false;

    let age = now.getFullYear() - dob.getFullYear();
    const monthDiff = now.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age -= 1;
    return age >= minAge;
  }

  setFieldError(id, message) {
    const errEl = this.root.querySelector(`[data-error-for="${id}"]`);
    const field = this.root.querySelector(`[data-field="${id}"]`);
    if (errEl) errEl.textContent = message || "";
    if (field) field.classList.toggle("mdi-intake__field--bad", !!message);
  }

  clearFieldError(id) {
    this.setFieldError(id, "");
  }

  /* ---------------- Outcomes ---------------- */

  renderOutcome(id, outcome) {
    if (this.isEducational) return this.renderEducationalOutcome(id, outcome);
    if (outcome.green) return this.renderGreen(outcome);
    this.root.innerHTML = "";

    const wrap = this.el("div", { class: "mdi-intake__outcome" });
    if (this.showTriage) wrap.appendChild(this.codeChip(outcome.code));
    // Red triage screens are kept clean and clinical: no decorative emoji, just the message and the
    // next step. Amber and other outcomes still show their icon.
    if (outcome.icon && outcome.code !== "red") {
      wrap.appendChild(this.el("div", { class: "mdi-intake__outcome-icon", text: outcome.icon }));
    }
    if (outcome.heading) wrap.appendChild(this.el("h2", { class: "mdi-intake__outcome-heading", text: outcome.heading }));
    if (outcome.lead) wrap.appendChild(this.el("p", { class: "mdi-intake__lead", text: outcome.lead }));
    if (outcome.quote) wrap.appendChild(this.el("blockquote", { class: "mdi-intake__quote", text: outcome.quote }));
    if (outcome.next) {
      const next = this.el("div", { class: "mdi-intake__nextstep" });
      next.appendChild(this.el("span", { class: "mdi-intake__nextstep-label", text: outcome.nextLabel || "Your next step" }));
      next.appendChild(this.el("p", { text: outcome.next }));
      wrap.appendChild(next);
    }
    wrap.insertBefore(this.printMeta(), wrap.firstChild);

    // A soft outcome: the shopper may continue anyway (the paid consult decides) or wait until later.
    // The fee line sits right above the choice so the cost is read before "Proceed anyway" is tapped.
    if (outcome.proceed && outcome.proceed.go) {
      if (outcome.fee) wrap.appendChild(this.el("p", { class: "mdi-intake__fee", text: outcome.fee }));
      const cta = this.el("div", { class: "mdi-intake__cta" });
      const proceed = this.el("button", {
        class: "mdi-intake__btn mdi-intake__btn--block",
        text: outcome.proceed.label || "Proceed anyway",
        attrs: { type: "button" },
      });
      proceed.addEventListener("click", () => this.proceedAnyway(id, outcome));
      cta.appendChild(proceed);
      wrap.appendChild(cta);
      wrap.appendChild(this.waitRow(outcome));
      const keepSoft = this.renderKeepInTouch(id, outcome);
      if (keepSoft) wrap.appendChild(keepSoft);
      this.root.appendChild(wrap);
      this.root.appendChild(this.backRow());
      return;
    }

    const keep = this.renderKeepInTouch(id, outcome);
    if (keep) wrap.appendChild(keep);
    wrap.appendChild(this.actionsRow());
    this.root.appendChild(wrap);
  }

  // "Proceed anyway" on a soft outcome. The answer that led here gets the outcome's prescriber flag so
  // the note travels with the record, and the outcome is dropped from history so Back from the next
  // question returns to the question, not to the warning. Routing is re-armed so the final terminal
  // outcome (green, or a later red) is the one posted to the CRM.
  proceedAnyway(id, outcome) {
    const flag = outcome.proceed.flag || null;
    const answeredId = this.answerOrder[this.answerOrder.length - 1];
    if (flag && answeredId) {
      this.flagByAnswer.set(answeredId, flag);
      this.recomputeFlags();
    }
    this.current = this.history.pop() || null;
    this._routed = false;
    this.dispatch("proceed", { outcome: id, code: outcome.code, flag, contact: this.leadCaptured });
    // The step leaves the outcome screen, not the question that led there, so the report keeps the
    // red screen in the path and can show how many proceeded past it.
    this._stepFromOverride = id;
    this._stepAnswer = outcome.proceed.label || "Proceed anyway";
    this.render(outcome.proceed.go);
  }

  // "Wait until later" on a soft outcome: prints the page so the shopper leaves with the result and
  // the next step in hand. Kept as its own row so the primary "Proceed anyway" stands alone above it.
  waitRow(outcome) {
    const row = this.el("div", { class: "mdi-intake__actions" });
    const wait = this.el("button", {
      class: "mdi-intake__btn mdi-intake__btn--ghost",
      text: outcome.waitLabel || (this.config.labels && this.config.labels.wait) || "Wait until later",
      attrs: { type: "button", "data-mdi-print": "" },
    });
    wait.addEventListener("click", () => this.printPage());
    row.appendChild(wait);
    return row;
  }

  // Optional email on a red / amber outcome. The shopper has not given an email yet (capture now
  // sits after the questions), so this is the only way a non-passing outcome can be followed up.
  // Labelled "Keep in touch": we cannot send them their results. Copy comes from meta.keepInTouch
  // (an outcome may override or disable it with its own keepInTouch block / false). Hidden on green,
  // in the educational quiz, and once an email is already captured.
  renderKeepInTouch(id, outcome) {
    const copy = outcome.keepInTouch !== undefined ? outcome.keepInTouch : (this.config.meta && this.config.meta.keepInTouch);
    if (!copy || outcome.green || this.isEducational || (this.leadCaptured && this.leadCaptured.email)) return null;
    const box = this.el("div", { class: "mdi-intake__keep" });
    box.appendChild(this.el("p", { class: "mdi-intake__keep-label", text: copy.label || "Keep in touch" }));
    if (copy.sub) box.appendChild(this.el("p", { class: "mdi-intake__keep-sub", text: copy.sub }));
    const row = this.el("div", { class: "mdi-intake__keep-row" });
    const input = this.el("input", {
      class: "mdi-intake__keep-input",
      attrs: { type: "email", inputmode: "email", autocomplete: "email", placeholder: copy.placeholder || "Email", "aria-label": copy.label || "Keep in touch", id: this.uid("keep_in_touch") },
    });
    const btn = this.el("button", { class: "mdi-intake__btn mdi-intake__btn--ghost", text: copy.button || "Sign up", attrs: { type: "button" } });
    const err = this.el("span", { class: "mdi-intake__field-error", attrs: { "aria-live": "polite" } });
    const submit = () => {
      const email = (input.value || "").trim();
      if (!MdiIntakeForm.isEmail(email)) {
        err.textContent = copy.error || "Enter a valid email address.";
        return;
      }
      err.textContent = "";
      this.keepInTouch(id, outcome, email);
      box.innerHTML = "";
      box.appendChild(this.el("p", { class: "mdi-intake__keep-done", text: copy.done || "Thanks, we'll be in touch." }));
    };
    btn.addEventListener("click", submit);
    input.addEventListener("keydown", (e) => {
      if (e.key !== "Enter" || e.isComposing) return;
      e.preventDefault();
      submit();
    });
    input.addEventListener("input", () => {
      err.textContent = "";
    });
    row.appendChild(input);
    row.appendChild(btn);
    box.appendChild(row);
    box.appendChild(err);
    return box;
  }

  // Hand the keep-in-touch email to the backend: the outcome's routing body again, now with a contact
  // and keepInTouch: true, so the orchestrator records a contact row and enrolls the outcome's journey.
  // De-duped per outcome. Sets leadCaptured so "Print page" can post the lead and the box hides.
  keepInTouch(id, outcome, email) {
    const key = id + "|" + email.toLowerCase();
    if (this._keepInTouchPosted === key) return;
    this._keepInTouchPosted = key;
    this.leadCaptured = { email };
    this._lastLead = JSON.stringify(this.leadCaptured);
    const event = {
      outcome: id,
      triageCode: outcome.code || null,
      outcomeCode: outcome.code || null,
      passed: false,
      market: this.market,
      mode: this.mode,
      contact: this.leadCaptured,
      brand: (this.config.meta && this.config.meta.brand) || null,
      sessionId: this.sessionId,
      keepInTouch: true,
    };
    this.dispatch("keep-in-touch", event);
    if (!this.routingUrl) return;
    this.postJson(this.routingUrl, event, "keep-in-touch");
  }

  renderGreen(outcome) {
    // Rolling 12-month one-per-customer block. A clinically-passing customer who is inside the window
    // (server-resolved starterKitIneligible) never gets the kit added or sent to checkout — we show the
    // limit screen instead of the proceed CTA. This gates the auto-add-then-checkout handoff that
    // replaced the old PDP add-to-cart. Fail-open: guests / eligible customers never reach this branch,
    // and the cart & checkout validation Function remains the hard limit.
    if (this.starterKitIneligible && outcome.action === "proceed" && !this.completeMode) {
      return this.renderLimit(outcome);
    }

    // Post-purchase mode: the shopper already ordered, so there is nothing to buy. Show a brief
    // saving state, then the intake-complete screen once the record save resolves.
    if (this.completeMode && outcome.action === "proceed") {
      return this.renderCompleteSaving(outcome);
    }

    this.root.innerHTML = "";
    const hasFlags = this.flags.length > 0;
    const loading = !!outcome.loading;

    const wrap = this.el("div", { class: "mdi-intake__outcome" });
    if (this.showTriage) {
      const code = this.recordTriageCode();
      wrap.appendChild(this.codeChip(code, hasFlags ? code + " (passed with prescriber notes)" : code));
    }

    // Loading / transition mode: a minimal screen the shopper only sees for a moment before checkout.
    if (loading) {
      wrap.appendChild(this.el("div", { class: "mdi-intake__spinner", attrs: { "aria-hidden": "true" } }));
    } else if (outcome.icon) {
      wrap.appendChild(this.el("div", { class: "mdi-intake__outcome-icon", text: outcome.icon }));
    }

    if (outcome.heading) wrap.appendChild(this.el("h2", { class: "mdi-intake__outcome-heading", text: outcome.heading }));
    if (outcome.sub) wrap.appendChild(this.el("p", { class: "mdi-intake__lead", text: outcome.sub }));

    // Full confirmation copy only when not a transition screen.
    if (!loading) {
      const lead = hasFlags ? outcome.leadFlagged : outcome.leadClean;
      if (lead) wrap.appendChild(this.el("p", { class: "mdi-intake__lead", text: lead }));
      if (outcome.quote) wrap.appendChild(this.el("blockquote", { class: "mdi-intake__quote", text: outcome.quote }));

      if (hasFlags) {
        const notes = this.el("div", { class: "mdi-intake__notes" });
        this.flags.forEach((flag) => {
          const text = (this.config.notes && this.config.notes[flag]) || flag;
          const note = this.el("div", { class: "mdi-intake__note" });
          note.appendChild(this.el("span", { class: "mdi-intake__note-dot" }));
          note.appendChild(this.el("span", { text }));
          notes.appendChild(note);
        });
        wrap.appendChild(notes);
      }

      if (outcome.next) {
        const next = this.el("div", { class: "mdi-intake__nextstep" });
        next.appendChild(this.el("span", { class: "mdi-intake__nextstep-label", text: outcome.nextLabel || "What happens now" }));
        next.appendChild(this.el("p", { text: outcome.next }));
        wrap.appendChild(next);
      }
    }

    // Commerce handoff on a passing outcome. The green screen is visible and waits for a click
    // (autoProceed: false in the shipped config): "Continue to checkout" adds the Starter Kit and
    // opens checkout. Account/login is handled by Shopify checkout, so it happens after intake. A
    // config that sets autoProceed (default true) still fires it automatically once the screen is
    // up. With no variant configured we fall back to the login URL if present.
    if (outcome.action === "proceed" && this.canProceed()) {
      const cta = this.el("div", { class: "mdi-intake__cta" });
      const proceed = this.el("button", {
        class: "mdi-intake__btn mdi-intake__btn--block",
        text: outcome.proceedLabel || "Continue to checkout",
        attrs: { type: "button" },
      });
      proceed.addEventListener("click", () => this.proceed(proceed, outcome));
      cta.appendChild(proceed);
      const status = this.el("p", { class: "mdi-intake__privacy", attrs: { "data-mdi-proceed-status": "" } });
      cta.appendChild(status);
      wrap.appendChild(cta);

      // Fire automatically once the confirmation is on screen. Guarded so a misconfig cannot loop.
      if (outcome.autoProceed !== false && this.starterKitVariantId && !this._autoProceedFired) {
        this._autoProceedFired = true;
        this.proceed(proceed, outcome);
      }
    }

    // Surface a failed record save as soon as the submit settles, whether or not the auto hand-off
    // fired (no variant configured, or a blocked redirect): the shopper must never sit on a spinner
    // with a failed record behind it. proceed() skips its own failure render once this has shown.
    if (outcome.action === "proceed" && this._submit) {
      const submit = this._submit;
      const shownOn = this.current;
      submit.then((result) => {
        if (result && !result.ok && this._submit === submit && this.current === shownOn && !this._submitFailureShown) {
          this.renderSubmitFailure(outcome, result);
        }
      });
    }

    // A transition screen has no actions; the green confirmation keeps "Start over" only. Green
    // results are not printable: there are no notes or contraindications to share with a provider.
    if (!loading) {
      wrap.appendChild(this.actionsRow({ print: false }));
    }
    this.root.appendChild(wrap);
  }

  // Post-purchase end flow: "Saving..." while the record submit is pending, then the complete
  // screen. A failed save goes to renderSubmitFailure. Guarded so a stale promise (restart, back)
  // cannot overwrite whatever screen is showing by then.
  renderCompleteSaving(outcome) {
    this.root.innerHTML = "";
    const copy = (outcome && outcome.complete) || {};
    const wrap = this.el("div", { class: "mdi-intake__outcome" });
    wrap.appendChild(this.el("div", { class: "mdi-intake__spinner", attrs: { "aria-hidden": "true" } }));
    wrap.appendChild(
      this.el("h2", {
        class: "mdi-intake__outcome-heading",
        text: copy.proceedingLabel || "Saving your answers for your clinician...",
      }),
    );
    this.root.appendChild(wrap);

    const submit = this._submit;
    if (!submit) {
      this.renderIntakeComplete(outcome);
      return;
    }
    const shownOn = this.current;
    submit.then((result) => {
      if (this._submit !== submit || this.current !== shownOn) return;
      if (result && !result.ok) {
        if (!this._submitFailureShown) this.renderSubmitFailure(outcome, result);
        return;
      }
      this.renderIntakeComplete(outcome);
    });
  }

  // Educational (non-medical market) outcome: the same copy as the medical outcome, but nothing is
  // gated and nothing is recorded (no MDI, no personal details). Red and amber read as advice, and
  // every outcome ends with one button that adds the Starter Kit and goes to checkout, the same
  // hand-off the medical flow uses. With no variant configured it falls back to the product page.
  renderEducationalOutcome(id, outcome) {
    this.root.innerHTML = "";
    const wrap = this.el("div", { class: "mdi-intake__outcome" });
    if (this.showTriage) wrap.appendChild(this.codeChip(outcome.code, `${outcome.code} (educational)`));
    if (this.marketConfig.eyebrow) wrap.appendChild(this.el("div", { class: "mdi-intake__eyebrow", text: this.marketConfig.eyebrow }));
    if (outcome.icon && outcome.code !== "red") {
      wrap.appendChild(this.el("div", { class: "mdi-intake__outcome-icon", text: outcome.icon }));
    }
    if (outcome.heading) wrap.appendChild(this.el("h2", { class: "mdi-intake__outcome-heading", text: outcome.heading }));
    if (outcome.lead) wrap.appendChild(this.el("p", { class: "mdi-intake__lead", text: outcome.lead }));
    if (outcome.quote) wrap.appendChild(this.el("blockquote", { class: "mdi-intake__quote", text: outcome.quote }));
    if (outcome.next) {
      const next = this.el("div", { class: "mdi-intake__nextstep" });
      next.appendChild(this.el("span", { class: "mdi-intake__nextstep-label", text: outcome.nextLabel || "Your next step" }));
      next.appendChild(this.el("p", { text: outcome.next }));
      wrap.appendChild(next);
    }
    const cta = this.el("div", { class: "mdi-intake__cta" });
    const purchase = this.el("button", {
      class: "mdi-intake__btn mdi-intake__btn--block",
      text: this.marketConfig.purchaseLabel || "Continue to checkout",
      attrs: { type: "button" },
    });
    const status = this.el("p", { class: "mdi-intake__sub", attrs: { "data-mdi-proceed-status": "", "aria-live": "polite" } });
    purchase.addEventListener("click", () => {
      if (this.starterKitVariantId) {
        purchase.disabled = true;
        purchase.textContent = outcome.proceedingLabel || "Adding your Starter Kit and taking you to checkout...";
        this.addKitAndCheckout(purchase, { ...outcome, proceedLabel: this.marketConfig.purchaseLabel || "Continue to checkout" }, status);
      } else {
        this.navigate(this.marketConfig.purchaseUrl || "/");
      }
    });
    cta.appendChild(purchase);
    cta.appendChild(status);
    wrap.appendChild(cta);
    // Red/amber advice is worth printing; a green quiz result is not.
    if (outcome.code !== "green") wrap.insertBefore(this.printMeta(), wrap.firstChild);
    wrap.appendChild(this.actionsRow({ print: outcome.code !== "green" }));
    this.root.appendChild(wrap);
    this.root.appendChild(this.backRow());
  }

  // Shown in place of the checkout handoff when a passing customer is inside their rolling 12-month
  // Starter Kit window, and (opts.entry) as the very first screen when the customer arrives already
  // ineligible, so the PDP's "Begin purchase" leads straight to the limit instead of a questionnaire
  // that cannot end in a purchase. Copy is config-driven (outcome.limit) with sensible fallbacks;
  // limit.entryLead replaces limit.lead at the entry gate (no "you are a great fit" before any answer).
  // Reuses the standard outcome layout so no new styling is needed. No cart add, no checkout redirect.
  renderLimit(outcome, opts = {}) {
    this.root.innerHTML = "";
    const limit = (outcome && outcome.limit) || {};

    const wrap = this.el("div", { class: "mdi-intake__outcome" });
    if (this.showTriage) wrap.appendChild(this.codeChip("green", "green (Starter Kit limit reached)"));
    if (limit.icon) wrap.appendChild(this.el("div", { class: "mdi-intake__outcome-icon", text: limit.icon }));
    wrap.appendChild(this.el("h2", {
      class: "mdi-intake__outcome-heading",
      text: limit.heading || "You already have a Starter Kit this year",
    }));
    const lead =
      (opts.entry && limit.entryLead) ||
      limit.lead ||
      "Our records show a Cntrl+ Starter Kit within the last 12 months. The Starter Kit is limited to one per customer every 12 months, so we can’t add another just yet.";
    wrap.appendChild(this.el("p", { class: "mdi-intake__lead", text: lead }));
    if (limit.quote) wrap.appendChild(this.el("blockquote", { class: "mdi-intake__quote", text: limit.quote }));

    const next = this.el("div", { class: "mdi-intake__nextstep" });
    next.appendChild(this.el("span", { class: "mdi-intake__nextstep-label", text: limit.nextLabel || "What happens now" }));
    next.appendChild(
      this.el("p", {
        text:
          limit.next ||
          "Your refills are always available from your account. Once your 12 months are up you can order a new Starter Kit here.",
      })
    );
    wrap.appendChild(next);

    // At the entry gate there is nothing to start over; the shopper never began the questionnaire.
    if (!opts.entry) wrap.appendChild(this.actionsRow({ print: false }));
    this.root.appendChild(wrap);
  }

  canProceed() {
    // Adding to cart works for guests and logged-in customers, so a variant is all we need. The
    // login URL is only a fallback for when no variant is configured.
    return !!this.starterKitVariantId || !!this.loginUrl;
  }

  proceed(button, outcome, opts = {}) {
    // No variant to add: fall back to the login URL if one is configured, otherwise do nothing.
    if (!this.starterKitVariantId) {
      if (this.loginUrl) this.navigate(this.loginUrl);
      return;
    }

    // Find the status line relative to the button so it works even during the initial auto-fire,
    // when the outcome subtree has not been attached to the card yet.
    const status = button && button.parentNode ? button.parentNode.querySelector("[data-mdi-proceed-status]") : null;
    const completeCopy = (outcome && outcome.complete) || {};
    const proceedingLabel = this.completeMode
      ? completeCopy.proceedingLabel || "Saving your answers for your clinician..."
      : (outcome && outcome.proceedingLabel) || "Adding your Starter Kit and taking you to checkout...";
    if (button) {
      button.disabled = true;
      button.textContent = proceedingLabel;
    }
    if (status) status.textContent = "";

    // Gate on the record submit (see sendToOrchestrator). skipSubmit is only passed after the
    // shopper confirmed on the failure screen that they want checkout without a saved record.
    const gate = opts.skipSubmit || !this._submit ? Promise.resolve({ ok: true }) : this._submit;
    gate.then((result) => {
      if (!result.ok) {
        if (!this._submitFailureShown) this.renderSubmitFailure(outcome, result);
        return;
      }
      if (this.completeMode) {
        this.renderIntakeComplete(outcome);
        return;
      }
      this.addKitAndCheckout(button, outcome, status);
    });
  }

  // Post-purchase mode's end screen: the record is saved, the orchestrator creates the MDI case for
  // the order they already placed, and there is nothing to buy. Copy from outcome.complete.
  renderIntakeComplete(outcome) {
    this._autoProceedFired = true;
    const copy = (outcome && outcome.complete) || {};
    this.root.innerHTML = "";
    const wrap = this.el("div", { class: "mdi-intake__outcome" });
    wrap.appendChild(this.el("h2", { class: "mdi-intake__outcome-heading", text: copy.heading || "Thanks, your answers are in" }));
    wrap.appendChild(
      this.el("p", {
        class: "mdi-intake__lead",
        text: copy.lead || "A clinician will review your intake for the Starter Kit you already ordered. Nothing else to do today.",
      }),
    );
    const next = this.el("div", { class: "mdi-intake__nextstep" });
    next.appendChild(this.el("span", { class: "mdi-intake__nextstep-label", text: copy.nextLabel || "What happens now" }));
    next.appendChild(
      this.el("p", {
        text: copy.next || "You will get an email once the clinician has reviewed your record. Your Starter Kit ships after approval.",
      }),
    );
    wrap.appendChild(next);
    this.root.appendChild(wrap);
    this.dispatch("intake-complete", { postPurchase: true });
  }

  // Checkout prefill: the intake already has the shopper's contact and shipping details, so the
  // Storefront API cart carries them as buyer identity and a pre-selected delivery address. Shopify
  // checkout then opens filled in. Nothing goes in a URL (no cart permalink params): the details
  // travel in a POST body to Shopify only. Optional keys are left out when blank.
  prefillInput() {
    const v = this.values || {};
    const text = (k) => (v[k] == null ? "" : String(v[k]).trim());
    const put = (obj, key, val) => {
      if (val) obj[key] = val;
      return obj;
    };
    const address = {};
    put(address, "firstName", text("first_name"));
    put(address, "lastName", text("last_name"));
    put(address, "address1", text("addr"));
    put(address, "address2", text("addr2"));
    put(address, "city", text("city"));
    put(address, "provinceCode", text("state"));
    put(address, "zip", text("zip"));
    address.countryCode = "US";
    const input = {};
    put(input, "email", text("email"));
    put(input, "phone", MdiIntakeForm.e164UsPhone(text("phone_number")));
    input.countryCode = "US";
    input.address = address;
    return input;
  }

  // Pinned Storefront API version for createPrefilledCart. Shopify supports a version for about a
  // year, then serves the oldest supported one instead; bump this before 2026-10 drops off.
  // Abort the Storefront call after this long so a hung request falls back to the Ajax cart.
  static STOREFRONT_TIMEOUT_MS = 8000;

  static get STOREFRONT_API_VERSION() {
    return "2026-10";
  }

  // Shopify wants E.164. The intake accepts "(512) 555-0100" and friends; anything that is not a
  // 10-digit US number (optionally with the leading 1) is left out rather than risk a userError.
  static e164UsPhone(raw) {
    const digits = String(raw || "").replace(/\D/g, "");
    if (digits.length === 10) return "+1" + digits;
    if (digits.length === 11 && digits[0] === "1") return "+" + digits;
    return "";
  }

  // Creates the Starter Kit cart through the Storefront API (cartCreate) and resolves to its
  // checkoutUrl. API 2026-10: buyerIdentity.deliveryAddressPreferences is deprecated and its
  // MailingAddressInput has no provinceCode, so the address goes in CartInput.delivery.addresses
  // (CartDeliveryAddressInput, selected + oneTimeUse). Rejects on an HTTP error, GraphQL errors,
  // userErrors, or a missing checkoutUrl; the caller falls back to the Ajax cart.
  createPrefilledCart() {
    const { address, ...identity } = this.prefillInput();
    const input = {
      lines: [
        {
          merchandiseId: `gid://shopify/ProductVariant/${this.starterKitVariantId}`,
          quantity: 1,
          // Same proof-of-intake token the Ajax path sends as a line property (see below).
          attributes: [{ key: "_intake", value: this.intakeToken || "intake" }],
        },
      ],
      buyerIdentity: identity,
      delivery: { addresses: [{ address: { deliveryAddress: address }, selected: true, oneTimeUse: true }] },
    };
    const query =
      "mutation($input: CartInput!) { cartCreate(input: $input) { cart { checkoutUrl } userErrors { field message } } }";
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), MdiIntakeForm.STOREFRONT_TIMEOUT_MS);
    return fetch(`https://${this.shopDomain}/api/${MdiIntakeForm.STOREFRONT_API_VERSION}/graphql.json`, {
      method: "POST",
      signal: ctrl.signal,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-Shopify-Storefront-Access-Token": this.storefrontToken,
      },
      body: JSON.stringify({ query, variables: { input } }),
    })
      .then((res) => {
        if (!res.ok) throw new Error(`cartCreate HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (json && Array.isArray(json.errors) && json.errors.length) {
          throw new Error(`cartCreate GraphQL error: ${json.errors.map((e) => e && e.message).join("; ")}`);
        }
        const payload = json && json.data && json.data.cartCreate;
        const userErrors = (payload && payload.userErrors) || [];
        if (userErrors.length) {
          throw new Error(`cartCreate userErrors: ${userErrors.map((e) => e && e.message).join("; ")}`);
        }
        const url = payload && payload.cart && payload.cart.checkoutUrl;
        if (!url) throw new Error("cartCreate returned no checkoutUrl");
        return url;
      })
      .finally(() => clearTimeout(timer));
  }

  addKitAndCheckout(button, outcome, status) {
    // US purchase with a Storefront token: build a prefilled cart and go to its checkout. Any failure
    // falls back to the standard Ajax cart below so nobody is stranded on the green screen.
    if (this.storefrontToken && this.shopDomain && !this.isEducational && !this.completeMode) {
      Promise.all([this.createPrefilledCart(), this.delay(1200)])
        .then(([url]) => this.navigate(url))
        .catch((err) => {
          console.error("[mdi-intake] prefilled cart failed, using standard cart", err);
          this.addKitAndCheckoutStandard(button, outcome, status);
        });
      return;
    }
    this.addKitAndCheckoutStandard(button, outcome, status);
  }

  addKitAndCheckoutStandard(button, outcome, status) {
    // Add the Starter Kit, then send them to checkout. A short minimum delay keeps the confirmation
    // on screen for a beat so the redirect does not feel abrupt.
    const added = fetch(this.cartAddUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      credentials: "same-origin",
      // `_intake` is the proof of intake the cart validation Function requires on every Starter Kit
      // line (hidden from the cart and checkout by the leading underscore). The orchestrator reads it
      // off the order to find the intake record even when the checkout email differs.
      body: JSON.stringify({
        items: [{ id: Number(this.starterKitVariantId), quantity: 1, properties: { _intake: this.intakeToken || "intake" } }],
      }),
    }).then((res) => {
      if (!res.ok) throw new Error(`cart/add HTTP ${res.status}`);
      return res;
    });

    Promise.all([added, this.delay(1200)])
      .then(() => this.navigate(this.checkoutUrl))
      .catch((err) => {
        console.error("[mdi-intake] add to cart failed", err);
        if (button) {
          button.disabled = false;
          button.textContent = outcome && outcome.proceedLabel ? outcome.proceedLabel : "Continue to checkout";
        }
        if (status) status.textContent = "Something went wrong adding your kit. Tap the button to try again.";
      });
  }

  // The record could not be saved (any MDI error, or the automatic retry also failed). Replace the
  // transition screen with a clear error, MDI's own message when it sent one ("Invalid date of
  // birth."), and the ways forward: edit the details, re-submit, or contact support. There is no
  // path to checkout from here: nobody buys a Starter Kit without an MDI patient behind the order
  // (US medical flow only; educational markets never get here).
  renderSubmitFailure(outcome, result) {
    this._submitFailureShown = true;
    this._autoProceedFired = true;
    const copy = (outcome && outcome.submitFailed) || {};
    this.root.innerHTML = "";
    const wrap = this.el("div", { class: "mdi-intake__outcome" });
    if (copy.icon) wrap.appendChild(this.el("div", { class: "mdi-intake__outcome-icon", text: copy.icon }));
    wrap.appendChild(this.el("h2", { class: "mdi-intake__outcome-heading", text: copy.heading || "We couldn't save your medical record" }));
    wrap.appendChild(
      this.el("p", {
        class: "mdi-intake__lead",
        attrs: { role: "alert" },
        text: copy.body || "Your answers are still here, but our system could not save them just now. Check your details and try again.",
      }),
    );
    // MDI's reason, verbatim, so the shopper knows what to fix (a validation error) rather than
    // re-submitting the same record.
    const mdiMessage = result && result.message;
    if (mdiMessage) {
      wrap.appendChild(
        this.el("blockquote", {
          class: "mdi-intake__quote",
          text: (copy.reasonPrefix || "The medical record system said: ") + mdiMessage,
        }),
      );
    }

    const cta = this.el("div", { class: "mdi-intake__cta" });
    // Straight back to the details form (the record form, the one with the review step), keeping
    // every answer, so a rejected field can be corrected and the record re-submitted from the review.
    const detailsId = this.recordFormId();
    if (detailsId) {
      const edit = this.el("button", {
        class: "mdi-intake__btn mdi-intake__btn--block",
        text: copy.editLabel || "Edit my details",
        attrs: { type: "button", "data-mdi-submit-edit": "" },
      });
      edit.addEventListener("click", () => {
        this._autoProceedFired = false;
        this._submitFailureShown = false;
        this._submit = null;
        this.editDetails(detailsId);
      });
      cta.appendChild(edit);
    }
    const retry = this.el("button", {
      class: "mdi-intake__btn mdi-intake__btn--ghost mdi-intake__btn--block mdi-intake__btn--retry",
      text: copy.retryLabel || "Try again",
      attrs: { type: "button", "data-mdi-submit-retry": "" },
    });
    const status = this.el("p", { class: "mdi-intake__privacy", attrs: { "data-mdi-proceed-status": "" } });
    retry.addEventListener("click", () => {
      retry.disabled = true;
      this._submitFailureShown = false;
      retry.textContent = copy.retryingLabel || "Saving your record...";
      status.textContent = "";
      this.sendToOrchestrator(this._lastPayload || this.buildPayload()).then((r) => {
        if (r.ok) {
          this.proceed(retry, outcome);
          return;
        }
        retry.disabled = false;
        retry.textContent = copy.retryLabel || "Try again";
        if (r.message) {
          status.textContent = (copy.reasonPrefix || "The medical record system said: ") + r.message;
        } else {
          status.textContent = copy.retryFailed || "Still not working. Check your details and try once more, or contact support.";
        }
      });
    });
    cta.appendChild(retry);
    cta.appendChild(status);

    cta.appendChild(
      this.el("p", {
        class: "mdi-intake__privacy",
        text: copy.supportLine || "Still stuck? Contact support@cntrlplus.com and we will sort it out.",
      }),
    );
    wrap.appendChild(cta);
    wrap.appendChild(this.actionsRow({ print: false }));
    this.root.appendChild(wrap);
  }

  // The record form: the form screen that carries the review step (form_details in the shipped
  // config). Used to send the shopper back to their details from the submit-failure screen.
  recordFormId() {
    const screens = this.config.screens || {};
    return Object.keys(screens).find((id) => screens[id].type === "form" && screens[id].review) || null;
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  navigate(url) {
    if (typeof window !== "undefined" && window.location) window.location.assign(url);
  }

  codeChip(code, labelOverride) {
    const chip = this.el("span", { class: "mdi-intake__chip mdi-intake__chip--" + code });
    chip.appendChild(this.el("span", { class: "mdi-intake__chip-dot" }));
    chip.appendChild(this.el("span", { text: "Internal triage code · " + (labelOverride || code).toUpperCase() }));
    return chip;
  }

  renderError() {
    if (!this.root) return;
    this.root.innerHTML = "";
    this.root.appendChild(this.el("p", { class: "mdi-intake__question", text: "We could not load this form right now. Please refresh the page." }));
  }

  /* ---------------- Shared controls ---------------- */

  backRow() {
    const row = this.el("div", { class: "mdi-intake__backrow" });
    const btn = this.el("button", { class: "mdi-intake__back", text: "← Back", attrs: { type: "button" } });
    if (!this.history.length) btn.setAttribute("hidden", "");
    btn.addEventListener("click", () => this.goBack());
    row.appendChild(btn);
    return row;
  }

  // Footer actions for a result screen: "Print page" (by default; off for green results) and
  // "Start over" (by default).
  actionsRow(opts = {}) {
    const row = this.el("div", { class: "mdi-intake__actions" });
    if (opts.print !== false) {
      const print = this.el("button", {
        class: "mdi-intake__btn mdi-intake__btn--ghost",
        text: (this.config.labels && this.config.labels.print) || "Print page",
        attrs: { type: "button", "data-mdi-print": "" },
      });
      print.addEventListener("click", () => this.printPage());
      row.appendChild(print);
    }
    if (opts.restart !== false) {
      const restart = this.el("button", { class: "mdi-intake__btn mdi-intake__btn--ghost", text: "Start over", attrs: { type: "button" } });
      restart.addEventListener("click", () => this.start());
      row.appendChild(restart);
    }
    return row;
  }

  /* ---------------- Printing ---------------- */

  // Print-only header line so a saved PDF carries context (what it is and when it was printed).
  // Hidden on screen by CSS; the date is filled in at print time.
  printMeta() {
    const meta = this.el("div", { class: "mdi-intake__print-meta", attrs: { "aria-hidden": "true" } });
    meta.appendChild(this.el("strong", { text: this.printTitle() }));
    meta.appendChild(this.el("span", { attrs: { "data-mdi-print-date": "" } }));
    return meta;
  }

  printTitle() {
    return (this.config.labels && this.config.labels.printTitle) || "Cntrl+ eligibility check";
  }

  // Opens the browser's native print dialog scoped to this form: everything outside the intake
  // element is hidden for the duration, the progress header and buttons are dropped by the print
  // stylesheet, and the document title is swapped so "Save as PDF" proposes a sensible filename.
  // No browser lets a page pre-select the PDF destination; Chrome/Edge/Safari remember the last one
  // the person used, so after the first "Save as PDF" it stays selected.
  printPage() {
    if (this._printing) return;
    this._printing = true;

    // Printing is the lead hand-off: the shopper is taking the result away, so this is the moment
    // to enroll them in the follow-up journey (not at intake start).
    this.postLead();

    const prevTitle = document.title;
    const today = new Date();
    const stamp = today.toISOString().slice(0, 10);
    document.title = `${this.printTitle()} ${stamp}`;
    const dateEl = this.root.querySelector("[data-mdi-print-date]");
    if (dateEl) dateEl.textContent = " · " + today.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });

    // Hide every sibling on the path from this element up to <body>, so only the form prints and
    // hidden page chrome does not leave blank pages behind (display:none, not visibility).
    const hidden = [];
    let node = this;
    while (node && node !== document.body) {
      const parent = node.parentElement;
      if (!parent) break;
      Array.from(parent.children).forEach((sib) => {
        if (sib !== node && !sib.hasAttribute("data-mdi-print-hide")) {
          sib.setAttribute("data-mdi-print-hide", "");
          hidden.push(sib);
        }
      });
      node = parent;
    }
    document.documentElement.classList.add("mdi-intake-printing");

    let restored = false;
    const restore = () => {
      if (restored) return;
      restored = true;
      window.removeEventListener("afterprint", restore);
      hidden.forEach((sib) => sib.removeAttribute("data-mdi-print-hide"));
      document.documentElement.classList.remove("mdi-intake-printing");
      document.title = prevTitle;
      this._printing = false;
    };
    window.addEventListener("afterprint", restore);

    try {
      window.print();
    } catch (err) {
      console.error("[mdi-intake] print failed:", err);
    }
    // afterprint fires once the dialog closes; the timeout is a safety net for browsers that don't
    // fire it (window.print() returns immediately there, so the page is not left half-hidden).
    setTimeout(restore, 1500);
  }

  /* ---------------- Utilities ---------------- */

  uid(fieldId) {
    return `mdi-${this.dataset.mdiUid || "default"}-${fieldId}`;
  }

  dispatch(name, detail) {
    this.dispatchEvent(new CustomEvent("mdi-intake:" + name, { bubbles: true, detail }));
  }

  el(tag, opts = {}) {
    const node = document.createElement(tag);
    if (opts.class) node.className = opts.class;
    if (opts.text != null) node.textContent = opts.text;
    if (opts.attrs) {
      Object.keys(opts.attrs).forEach((key) => node.setAttribute(key, opts.attrs[key]));
    }
    return node;
  }
}

if (!customElements.get("mdi-intake-form")) {
  customElements.define("mdi-intake-form", MdiIntakeForm);
}
